import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as d3 from 'd3';
import { ShieldCheck, AlertTriangle, GitBranch, ClipboardList, GitMerge, Plus, Trash2, Sparkles, Search } from 'lucide-react';
import type { Remark, RemarksDashboardResponse, Snapshot } from '@/types/api';
import { normSeverity } from '@/types/api';
import { api } from '@/api/client';
import { CompletenessPanel } from './CompletenessPanel';
import { TepReconcilePanel } from './TepReconcilePanel';
import { NormsActualityPanel } from './NormsActualityPanel';
import { DecisionImpactPanel } from './DecisionImpactPanel';
import { ReadinessOverview } from './ReadinessOverview';
import { TaskRequirementsPanel } from './TaskRequirementsPanel';
import { TepRegistryPanel } from './TepRegistryPanel';
import { TaskComplianceResults } from './TaskComplianceResults';
import { FrozenView } from './FrozenView';
import { FindingsLock } from './FindingsLock';
import { PaymentModal } from './PaymentModal';
import { useIntegrityVotes } from '@/hooks/useIntegrityVotes';
import { useAuth } from '@/state/auth';
import type { Project } from '@/types/api';

// 15.F freemium: форма сводки readiness для счётчиков на замках находок.
interface RsCounts {
  completeness?: { absent?: number; has_result?: boolean };
  norms?: { problems?: number; has_result?: boolean };
  decisions?: { count?: number };
  task_compliance?: { problems?: number; has_result?: boolean };
  tep?: { problems?: number; has_result?: boolean };
}

type ViewId = 'overview' | 'radar' | 'graph' | 'conflicts' | 'cascade' | 'waves' | 'compliance' | 'norms' | 'decisions' | 'tep' | 'audit' | 'routing' | 'summary' | 'aiview';

interface Props {
  remarks: Remark[];
  summary?: RemarksDashboardResponse['summary'];
  projectId: string;
  snapshotId: string | null;
  snapshots: Snapshot[];
  onGoToRemarks?: () => void;
}

// Вкладки в разработке — показываем FrozenView с голосованием.
// Чтобы «разморозить» вкладку — просто удалите её из этого массива.
const FROZEN_VIEWS = new Set<ViewId>(['graph', 'conflicts', 'cascade', 'waves', 'audit', 'routing', 'summary', 'aiview']);

const ALL_VIEWS: { id: ViewId; label: string }[] = [
  { id: 'overview',    label: 'Обзор' },
  { id: 'radar',       label: 'Радар проверок' },
  { id: 'graph',       label: 'Граф связей' },
  { id: 'conflicts',   label: 'Конфликты' },
  { id: 'cascade',     label: 'Зона влияния' },
  { id: 'waves',       label: 'Сравнение редакций' },
  { id: 'compliance',  label: 'Соответствие нормам' },
  { id: 'norms',       label: 'Актуальность норм' },
  { id: 'decisions',   label: 'Проектное решение' },
  { id: 'audit',       label: 'Журнал аудита' },
  { id: 'routing',     label: 'Маршрутизация' },
  { id: 'summary',     label: 'Сводка' },
  { id: 'aiview',      label: 'ИИ-вид' },
];

export function IntegrityTab({ remarks, summary, projectId, snapshotId, snapshots, onGoToRemarks }: Props) {
  // Замороженные (locked) под-виды показываем ТОЛЬКО админу; кураторам/ГИПам/
  // экспертам их вообще не рендерим (ни плитки, ни контент по прямому URL).
  const role = useAuth((s) => s.user?.default_role);
  const isAdmin = role === 'admin';
  // Заказчик (приёмка): Обзор + Block A + Сверка ТЭП + Сравнение редакций.
  // Свой набор с ярлыками (в т.ч. 'tep', которого нет в ALL_VIEWS — он живёт
  // у остальных ролей во вкладке «Запрос»). Всё рабочее, без замков.
  const isCustomer = role === 'customer';

  // 15.F freemium: детали находок заказчик видит только после оплаты тарифа.
  // Цифры (сводка) — бесплатны; здесь гейтим 4 вида находок и открываем оплату.
  const [payOpen, setPayOpen] = useState(false);
  const projectQ = useQuery<Project>({
    queryKey: ['project', projectId],
    queryFn: () => api.get<Project>(`/projects/${projectId}`),
    enabled: !!projectId && isCustomer,
    staleTime: 30_000,
  });
  const tariff = (projectQ.data as { tariff?: string | null } | undefined)?.tariff ?? null;
  const paid = tariff === 'express' || tariff === 'full';
  const locked = isCustomer && !paid;
  const rsQ = useQuery<RsCounts>({
    queryKey: ['readiness-summary', projectId],
    queryFn: () => api.get<RsCounts>(`/projects/${projectId}/readiness-summary`),
    enabled: !!projectId && locked,
    staleTime: 30_000,
  });
  const rs = rsQ.data;
  // Счётчик для замка: null (не проверяли) → «проверка не запускалась».
  const lockCount = (v: ViewId): number | null => {
    if (v === 'compliance') return rs?.completeness?.has_result ? (rs.completeness.absent ?? 0) : null;
    if (v === 'norms') return rs?.norms?.has_result ? (rs.norms.problems ?? 0) : null;
    if (v === 'decisions') return rs?.norms?.has_result ? (rs.decisions?.count ?? 0) : null;
    if (v === 'tep') return rs?.task_compliance?.has_result
      ? (rs.task_compliance.problems ?? 0)
      : (rs?.tep?.has_result ? (rs.tep.problems ?? 0) : null);
    return null;
  };
  const LOCK_TITLE: Partial<Record<ViewId, string>> = {
    compliance: 'Состав документации', norms: 'Актуальность норм',
    decisions: 'Проектное решение', tep: 'Соответствие заданию',
  };

  // Имена — языком заказчика-инвестора (утверждено 2026-07-02): его вопросы
  // «каковы шансы пройти?» / «всё ли сдали?» / «то ли запроектировали?» / «что исправили?»
  // Порядок — решение владельца 2026-07-03; «Сравнение редакций» ушло
  // на верхний уровень (отдельный таб), внутри «Готовности» его больше нет.
  // Порядок под-разделов заказчика (решение владельца 2026-07-05).
  const CUSTOMER_VIEW_DEFS: { id: ViewId; label: string }[] = [
    { id: 'overview',   label: 'Оценка готовности' },
    { id: 'tep',        label: 'Соответствие заданию' },
    { id: 'decisions',  label: 'Проектное решение' },
    { id: 'compliance', label: 'Состав документации' },
    { id: 'norms',      label: 'Актуальность норм' },
  ];

  // Незамороженные (рабочие) виды — верхний ряд у всех. Замороженные (замок) —
  // отдельным рядом НИЖЕ и ТОЛЬКО админу; остальным их не рендерим совсем.
  // Для заказчика набор фиксированный, ничего не заморожено.
  const effectiveFrozen = isCustomer ? new Set<ViewId>() : FROZEN_VIEWS;
  const unlockedViews = isCustomer
    ? CUSTOMER_VIEW_DEFS
    : ALL_VIEWS.filter((v) => !FROZEN_VIEWS.has(v.id));
  const frozenViews = isCustomer ? [] : ALL_VIEWS.filter((v) => FROZEN_VIEWS.has(v.id));

  const [view, setView] = useState<ViewId>(() => {
    const u = new URLSearchParams(window.location.search);
    const v = u.get('view');
    const allowed = (isAdmin ? ALL_VIEWS : unlockedViews).map((x) => x.id);
    return (allowed.includes(v as ViewId) ? v : 'overview') as ViewId;
  });

  // Если роль догрузилась и текущий вид оказался недоступен — на «Обзор».
  useEffect(() => {
    const allowed = (isAdmin ? ALL_VIEWS : unlockedViews).map((x) => x.id);
    if (!allowed.includes(view)) setView('overview');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isCustomer, view]);

  useEffect(() => {
    const u = new URL(window.location.href);
    u.searchParams.set('view', view);
    window.history.replaceState(null, '', u.toString());
  }, [view]);

  // Хук для голосования на замороженных вкладках (Вариант A)
  const { votes, castVote } = useIntegrityVotes(projectId);

  /** Если вкладка в разработке — показываем FrozenView вместо реального контента */
  function renderFrozenOr(viewId: ViewId, content: React.ReactNode) {
    if (effectiveFrozen.has(viewId)) {
      return (
        <FrozenView
          viewId={viewId}
          counts={votes[viewId]}
          onVote={(value) => castVote(viewId, value)}
        />
      );
    }
    return content;
  }

  return (
    <div>
      {/* У заказчика вкладка уже называется «Готовность к экспертизе» — заголовок
          внутри дублировал бы её. Шапку показываем только куратору/админу. */}
      {!isCustomer && (
        <header style={{ marginBottom: 24 }}>
          <h3 className="font-heading text-ink" style={{ fontSize: 20, fontWeight: 600, letterSpacing: '-0.01em' }}>
            Целостность проектного решения
          </h3>
          <p className="text-muted mt-1" style={{ fontSize: 13, maxWidth: 720 }}>
            Проверка соответствия проектных решений и комплектности документации требованиям 87-ФЗ
          </p>
        </header>
      )}

      {/* Верхний ряд — рабочие (незамороженные) виды */}
      <Segmented
        active={view}
        onChange={setView}
        items={unlockedViews}
        frozenIds={[]}
        voteCounts={votes}
      />
      {/* Нижний ряд — замороженные (замок), ТОЛЬКО админу */}
      {isAdmin && (
        <div style={{ marginTop: 8 }}>
          <Segmented
            active={view}
            onChange={setView}
            items={frozenViews}
            frozenIds={[...FROZEN_VIEWS]}
            voteCounts={votes}
          />
        </div>
      )}

      <div style={{ marginTop: 24 }}>
        {view === 'overview' && (
          isCustomer
            ? <ReadinessOverview projectId={projectId} onNavigate={(v) => setView(v as ViewId)} onGoToRemarks={onGoToRemarks} tariff={tariff} onPay={() => setPayOpen(true)} />
            : <OverviewView summary={summary} remarks={remarks} />
        )}
        {view === 'radar'      && <RadarView remarks={remarks} projectId={projectId} />}
        {view === 'graph'      && renderFrozenOr('graph',      <GraphView projectId={projectId} snapshotId={snapshotId} remarks={remarks} />)}
        {view === 'conflicts'  && renderFrozenOr('conflicts',  <ConflictsView remarks={remarks} projectId={projectId} />)}
        {view === 'cascade'    && renderFrozenOr('cascade',    <CascadeView remarks={remarks} projectId={projectId} />)}
        {view === 'waves'      && renderFrozenOr('waves',      <WavesView projectId={projectId} snapshots={snapshots} />)}
        {view === 'compliance' && (locked
          ? <FindingsLock title={LOCK_TITLE.compliance!} count={lockCount('compliance')} onPay={() => setPayOpen(true)} />
          : renderFrozenOr('compliance', <CompletenessPanel projectId={projectId} />))}
        {view === 'norms'      && (locked
          ? <FindingsLock title={LOCK_TITLE.norms!} count={lockCount('norms')} onPay={() => setPayOpen(true)} />
          : <NormsActualityPanel projectId={projectId} />)}
        {view === 'decisions'  && (locked
          ? <FindingsLock title={LOCK_TITLE.decisions!} count={lockCount('decisions')} onPay={() => setPayOpen(true)} />
          : <DecisionImpactPanel projectId={projectId} />)}
        {view === 'tep'        && (locked
          ? <FindingsLock title={LOCK_TITLE.tep!} count={lockCount('tep')} onPay={() => setPayOpen(true)} />
          : (
          <div>
            {/* Порядок «А» (решение владельца 2026-07-03, вечер): сверху вниз
                падает концентрация «про задание» — 1) измеримые ТРЕБОВАНИЯ
                задания против факта, 2) заключение по всем пунктам (список
                свёрнут), 3) ТЭП объекта (ПЗ ↔ разделы) справочно, 4) сервисные
                реестры. Показатель с требованием живёт только в блоке 1. */}
            <TepReconcilePanel projectId={projectId} mode="requirements" />
            <TaskComplianceResults projectId={projectId} />
            <TepReconcilePanel projectId={projectId} mode="passport" />
            <TepRegistryPanel projectId={projectId} />
            <TaskRequirementsPanel projectId={projectId} />
          </div>
          ))}
        {view === 'audit'      && renderFrozenOr('audit',      <AuditLogView projectId={projectId} />)}
        {view === 'routing'    && renderFrozenOr('routing',    <RoutingView projectId={projectId} snapshotId={snapshotId} remarks={remarks} />)}
        {view === 'summary'    && renderFrozenOr('summary',    <SummaryView projectId={projectId} snapshotId={snapshotId} remarks={remarks} summary={summary} snapshots={snapshots} />)}
        {view === 'aiview'     && renderFrozenOr('aiview',     <AIView remarks={remarks} />)}
      </div>

      {payOpen && <PaymentModal projectId={projectId} onClose={() => setPayOpen(false)} />}
    </div>
  );
}

import type { ViewVoteCounts } from '@/hooks/useIntegrityVotes';

function Segmented<T extends string>({
  active,
  onChange,
  items,
  frozenIds = [],
  voteCounts = {},
}: {
  active: T;
  onChange: (v: T) => void;
  items: { id: T; label: string }[];
  frozenIds?: T[];
  voteCounts?: Record<string, ViewVoteCounts>;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, padding: 4, background: 'var(--color-surface,#F3F4F6)', borderRadius: 32, width: 'fit-content' }}>
      {items.map((it) => {
        const isActive = active === it.id;
        const isFrozen = frozenIds.includes(it.id);
        const userVoted = isFrozen && voteCounts[it.id]?.user_vote;

        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onChange(it.id)}
            title={isFrozen ? 'Функция в разработке — нажмите, чтобы проголосовать' : undefined}
            style={{
              padding: '8px 14px',
              background: isActive ? '#FFFFFF' : 'transparent',
              boxShadow: isActive ? '0 1px 3px rgba(10,22,40,.08)' : 'none',
              fontWeight: isActive ? 600 : 500,
              fontSize: 13,
              color: isActive ? '#0A1628' : isFrozen ? '#9CA3AF' : '#6B7280',
              border: 'none',
              cursor: 'pointer',
              borderRadius: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              transition: 'color .12s',
            }}
          >
            {isFrozen && (
              <span style={{ fontSize: 11, opacity: 0.7 }}>
                {userVoted ? '✓' : '🔒'}
              </span>
            )}
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

/* ─── EmptyState ────────────────────────────────────────────────────────── */
function EmptyState({ icon: Icon, title, description }: {
  icon: typeof ShieldCheck; title: string; description: string;
}) {
  return (
    <div className="bg-surface rounded-card text-center" style={{ padding: '48px 32px', border: '1px dashed #E5E7EB' }}>
      <div className="mx-auto rounded-pill flex items-center justify-center" style={{
        width: 56, height: 56, background: 'rgba(107,114,128,.08)', marginBottom: 16,
      }}>
        <Icon size={24} stroke="#6B7280" />
      </div>
      <h4 className="font-heading text-ink" style={{ fontSize: 16, fontWeight: 600 }}>{title}</h4>
      <p className="text-muted mt-2 mx-auto" style={{ fontSize: 13, maxWidth: 520, lineHeight: 1.6 }}>{description}</p>
    </div>
  );
}

/* ─── Overview ──────────────────────────────────────────────────────────── */
interface OverviewViewProps {
  summary?: RemarksDashboardResponse['summary'];
  remarks: Remark[];
}

function OverviewView({ summary, remarks }: OverviewViewProps) {
  const isOpen = (r: Remark) => (r.remark_status || (r.closed_at ? 'closed' : 'open')) !== 'closed';
  const sOpen = remarks.filter((r) => r.defect_type === 'S-defect' && isOpen(r)).length;
  const eOpen = remarks.filter((r) => r.defect_type === 'E-gap' && isOpen(r)).length;
  const closed = remarks.filter((r) => !isOpen(r)).length;
  const sections = new Set(remarks.map((r) => r.section_code).filter(Boolean)).size;

  const sDefects = summary?.s_defects ?? sOpen;
  const eGaps = summary?.e_gaps ?? eOpen;
  const totalClosed = summary?.closed ?? closed;

  const sVerified = sDefects === 0;
  const eOk = eGaps === 0;
  return (
    <div>
      <div className="grid grid-cols-2" style={{ gap: 24 }}>
        <Banner
          icon={ShieldCheck}
          tone={sVerified ? 'emerald' : 'amber'}
          eyebrow="Проектное решение"
          headline={sVerified ? 'Блокирующих замечаний нет' : `Блокирующих: ${sDefects}`}
          subhead={sVerified ? 'По данным замечаний на текущей редакции' : 'Есть открытые блокирующие замечания'}
          metrics={[
            { label: 'Блокирующих', value: String(sDefects), color: sDefects > 0 ? '#EF4444' : '#10B981' },
            { label: 'Закрытых',   value: String(totalClosed), color: '#10B981' },
            { label: 'Разделов',   value: sections > 0 ? String(sections) : '—' },
          ]}
        />
        <Banner
          icon={AlertTriangle}
          tone={eOk ? 'emerald' : 'amber'}
          eyebrow="Комплектность документации"
          headline={eOk ? 'Устранимых замечаний нет' : `${eGaps} ${plural(eGaps, 'устранимое', 'устранимых', 'устранимых')}`}
          subhead={eOk ? 'Все известные требования комплектности закрыты' : 'Есть открытые устранимые замечания'}
          metrics={[
            { label: 'Устранимых', value: String(eGaps), color: eGaps > 0 ? '#F59E0B' : '#10B981' },
            { label: 'Закрытых',   value: String(totalClosed), color: '#10B981' },
            { label: 'Разделов',   value: sections > 0 ? String(sections) : '—' },
          ]}
        />
      </div>
      <div className="rounded-card text-muted" style={{ marginTop: 24, padding: 20, background: '#FFFFFF', border: '1px dashed #E5E7EB', fontSize: 13, lineHeight: 1.6 }}>
        Конкретные категории проверок целостности (геометрия, профиль, габариты, разделы 87-ФЗ и т.д.)
        будут показаны после полной сортировки замечаний и завершения каталога нормативных требований.
      </div>
    </div>
  );
}

function Banner({ icon: Icon, tone, eyebrow, headline, subhead, metrics }: {
  icon: typeof ShieldCheck; tone: 'emerald' | 'amber'; eyebrow: string; headline: string; subhead: string;
  metrics: { label: string; value: string; color?: string }[];
}) {
  const bg = tone === 'emerald' ? 'rgba(16,185,129,.05)' : 'rgba(245,158,11,.05)';
  const border = tone === 'emerald' ? 'rgba(16,185,129,.2)' : 'rgba(245,158,11,.2)';
  const iconColor = tone === 'emerald' ? '#10B981' : '#F59E0B';
  const headlineColor = tone === 'emerald' ? '#10B981' : '#F59E0B';
  return (
    <div className="rounded-card" style={{ padding: 24, background: bg, border: `1px solid ${border}` }}>
      <div className="flex items-start" style={{ gap: 16 }}>
        <Icon size={32} stroke={iconColor} />
        <div style={{ flex: 1 }}>
          <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>{eyebrow}</div>
          <h3 className="font-heading mt-1" style={{ fontSize: 20, fontWeight: 600, color: headlineColor }}>{headline}</h3>
          <p className="text-muted mt-1" style={{ fontSize: 13 }}>{subhead}</p>
        </div>
      </div>
      <div className="border-t mt-4 pt-4 grid grid-cols-3" style={{ gap: 16, borderColor: border }}>
        {metrics.map((m) => (
          <div key={m.label}>
            <div className="text-muted uppercase tracking-wider" style={{ fontSize: 11, fontWeight: 600 }}>{m.label}</div>
            <div className="font-heading tabular mt-1" style={{ fontSize: 22, fontWeight: 600, color: m.color || '#0A1628' }}>{m.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Radar ─────────────────────────────────────────────────────────────── */
type RadarAxis = { name: string; total: number; closed: number; value: number };

// ── PP87 section catalog (фиксированная основа радара) ─────────────────────
const PP87_SECTIONS: Array<{ code: string; name: string }> = [
  { code: 'ПЗ',  name: 'Пояснительная записка' },
  { code: 'ППО', name: 'Схема план. организации' },
  { code: 'ТКР', name: 'Конструктивные решения' },
  { code: 'ПЖ',  name: 'Путевое хозяйство' },
  { code: 'ПОС', name: 'Организация строительства' },
  { code: 'ООС', name: 'Охрана окружающей среды' },
  { code: 'ПБ',  name: 'Пожарная безопасность' },
  { code: 'СМ',  name: 'Сметная документация' },
  { code: 'ИЛО', name: 'Интелл. системы / ЖАТ' },
  { code: 'ТБЭ', name: 'Технологии эксплуатации' },
  { code: 'ГТ',  name: 'Геотехника' },
  { code: 'ВК',  name: 'Водоснабжение / канализация' },
];

interface PP87Section {
  code: string;
  status: 'present' | 'missing' | 'optional_missing';
  found_documents: Array<{ document_id: string; title: string }>;
}

interface PP87Response {
  sections?: PP87Section[];
  needs_object_type?: boolean;
  message?: string;
}

function RadarView({ remarks, projectId }: { remarks: Remark[]; projectId: string }) {
  const [selectedSection, setSelectedSection] = useState<string | null>(null);

  const { data: pp87Data } = useQuery<PP87Response>({
    queryKey: ['pp87-compliance', projectId],
    queryFn: () => api.get<PP87Response>(`/projects/${projectId}/compliance/pp87`),
    retry: false,
    staleTime: 60_000,
  });

  // Build section stats from remarks
  const remarksBySection = useMemo(() => {
    const byCode: Record<string, { open_s: number; open_e: number; open_total: number; closed: number }> = {};
    for (const r of remarks) {
      const code = (r.section_code || '').toUpperCase().split(/[-./]/)[0].trim();
      if (!code) continue;
      const isClosed = (r.remark_status || (r.closed_at ? 'closed' : 'open')) === 'closed';
      byCode[code] ||= { open_s: 0, open_e: 0, open_total: 0, closed: 0 };
      if (isClosed) {
        byCode[code].closed += 1;
      } else {
        byCode[code].open_total += 1;
        if (r.defect_type === 'S-defect') byCode[code].open_s += 1;
        else byCode[code].open_e += 1;
      }
    }
    return byCode;
  }, [remarks]);

  // Build PP87 status map from API
  const pp87ByCode = useMemo(() => {
    const m: Record<string, PP87Section> = {};
    for (const s of pp87Data?.sections ?? []) {
      m[s.code] = s;
    }
    return m;
  }, [pp87Data]);

  // Build axes: always use PP87_SECTIONS as base
  const axes = useMemo(() => {
    return PP87_SECTIONS.map((sec) => {
      const pp87 = pp87ByCode[sec.code];
      const rmk = remarksBySection[sec.code] || { open_s: 0, open_e: 0, open_total: 0, closed: 0 };
      const hasDoc = pp87 ? pp87.status === 'present' : false;
      const hasMandatoryMissing = pp87 ? pp87.status === 'missing' : false;
      return {
        code: sec.code,
        name: sec.name,
        hasDoc,
        hasMandatoryMissing,
        open_s: rmk.open_s,
        open_e: rmk.open_e,
        open_total: rmk.open_total,
        closed: rmk.closed,
      };
    });
  }, [pp87ByCode, remarksBySection]);

  const maxOpen = Math.max(...axes.map((a) => a.open_total), 1);

  const size = 520;
  const cx = size / 2;
  const cy = size / 2;
  const R_BASE = 150;
  const rings = [0.25, 0.5, 0.75, 1];
  const N = axes.length;

  const sectorPoints = axes.map((a, i) => {
    const angle = -Math.PI / 2 + (2 * Math.PI * i) / N;
    // Sector radius: base + intensity bump based on open remarks
    const intensityR = R_BASE * (0.3 + 0.7 * (a.open_total / maxOpen));
    return {
      angle,
      ax: cx + R_BASE * Math.cos(angle),
      ay: cy + R_BASE * Math.sin(angle),
      ix: cx + intensityR * Math.cos(angle),
      iy: cy + intensityR * Math.sin(angle),
      labelX: cx + (R_BASE + 28) * Math.cos(angle),
      labelY: cy + (R_BASE + 28) * Math.sin(angle),
      axis: a,
      intensityR,
    };
  });

  // SVG pie-like sectors for layer 1 (doc presence)
  function sectorPath(i: number, rInner: number, rOuter: number): string {
    const n = N;
    const a1 = -Math.PI / 2 + (2 * Math.PI * i) / n - Math.PI / n + 0.04;
    const a2 = -Math.PI / 2 + (2 * Math.PI * i) / n + Math.PI / n - 0.04;
    const x1 = cx + rOuter * Math.cos(a1);
    const y1 = cy + rOuter * Math.sin(a1);
    const x2 = cx + rOuter * Math.cos(a2);
    const y2 = cy + rOuter * Math.sin(a2);
    const x3 = cx + rInner * Math.cos(a2);
    const y3 = cy + rInner * Math.sin(a2);
    const x4 = cx + rInner * Math.cos(a1);
    const y4 = cy + rInner * Math.sin(a1);
    return `M ${x1} ${y1} A ${rOuter} ${rOuter} 0 0 1 ${x2} ${y2} L ${x3} ${y3} A ${rInner} ${rInner} 0 0 0 ${x4} ${y4} Z`;
  }

  const selectedAxis = selectedSection ? axes.find((a) => a.code === selectedSection) : null;

  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>Радар проверок — три слоя</h4>
        <div className="flex items-center" style={{ gap: 16, fontSize: 11, flexWrap: 'wrap' }}>
          <span className="flex items-center" style={{ gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#10B98130', border: '1px solid #10B981', display: 'inline-block' }} />Документ загружен</span>
          <span className="flex items-center" style={{ gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: '#6B728018', border: '1px solid #E5E7EB', display: 'inline-block' }} />Документ отсутствует</span>
          <span className="flex items-center" style={{ gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 10, background: '#F59E0B', display: 'inline-block' }} />Устранимые</span>
          <span className="flex items-center" style={{ gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 10, background: '#EF4444', display: 'inline-block' }} />Блокирующие</span>
        </div>
      </div>

      <div className="flex" style={{ gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <svg width={size} height={size} role="img" aria-label="Радар проверок три слоя">
          {/* кольца */}
          {rings.map((r) => (
            <circle key={r} cx={cx} cy={cy} r={R_BASE * r} fill="none" stroke="#E5E7EB" strokeDasharray="3 3" strokeWidth={r === 1 ? 1.5 : 1} />
          ))}
          {/* Layer 1: doc presence sectors */}
          {sectorPoints.map((p, i) => (
            <path
              key={`sec-${i}`}
              d={sectorPath(i, R_BASE * 0.15, R_BASE)}
              fill={p.axis.hasDoc ? '#10B98118' : '#6B728008'}
              stroke={p.axis.hasDoc ? '#10B98144' : '#E5E7EB'}
              strokeWidth={1}
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedSection(selectedSection === p.axis.code ? null : p.axis.code)}
            >
              <title>{p.axis.name}: {p.axis.hasDoc ? 'документ загружен' : 'документ отсутствует'}</title>
            </path>
          ))}
          {/* Layer 3: intensity blob */}
          {sectorPoints.map((p, i) => {
            if (p.axis.open_total === 0) return null;
            const color = p.axis.open_s > 0 ? '#EF4444' : '#F59E0B';
            return (
              <path
                key={`int-${i}`}
                d={sectorPath(i, R_BASE * 0.15, p.intensityR)}
                fill={`${color}22`}
                stroke={color}
                strokeWidth={p.axis.open_s > 0 ? 2.5 : 1.5}
                strokeDasharray={p.axis.open_s > 0 ? undefined : '4 2'}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedSection(selectedSection === p.axis.code ? null : p.axis.code)}
              >
                <title>
                  {p.axis.name}: {p.axis.open_s} блокирующих + {p.axis.open_e} устранимых открытых
                </title>
              </path>
            );
          })}
          {/* Оси */}
          {sectorPoints.map((p, i) => (
            <line key={`ax-${i}`} x1={cx} y1={cy} x2={p.ax} y2={p.ay} stroke="#D1D5DB" strokeWidth={0.5} />
          ))}
          {/* Подписи */}
          {sectorPoints.map((p, i) => {
            const anchor = Math.abs(p.labelX - cx) < 10 ? 'middle' : p.labelX > cx ? 'start' : 'end';
            const color = p.axis.open_s > 0 ? '#B91C1C' : p.axis.open_e > 0 ? '#92400E' : p.axis.hasMandatoryMissing ? '#6B7280' : '#374151';
            return (
              <text
                key={`lbl-${i}`}
                x={p.labelX} y={p.labelY}
                textAnchor={anchor} dominantBaseline="middle"
                fontSize={11} fontWeight={p.axis.open_s > 0 ? 700 : 500}
                fill={color}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedSection(selectedSection === p.axis.code ? null : p.axis.code)}
              >
                {p.axis.code}
              </text>
            );
          })}
          {/* Centre label */}
          <text x={cx} y={cy - 6} textAnchor="middle" fontSize={10} fill="#9CA3AF">PP87</text>
        </svg>

        {/* Side panel */}
        <div style={{ flex: 1, minWidth: 200 }}>
          {selectedAxis ? (
            <div style={{ padding: 16, borderRadius: 8, border: '1px solid #E5E7EB', background: '#F9FAFB' }}>
              <h5 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 8px', color: '#0A1628' }}>
                {selectedAxis.code} — {selectedAxis.name}
              </h5>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="text-muted">Документ:</span>
                  <span style={{ fontWeight: 600, color: selectedAxis.hasDoc ? '#10B981' : '#EF4444' }}>
                    {selectedAxis.hasDoc ? '✓ загружен' : '✗ отсутствует'}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="text-muted">Блокирующих открытых:</span>
                  <span style={{ fontWeight: 700, color: selectedAxis.open_s > 0 ? '#EF4444' : '#9CA3AF' }}>{selectedAxis.open_s}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="text-muted">Устранимых открытых:</span>
                  <span style={{ fontWeight: 700, color: selectedAxis.open_e > 0 ? '#F59E0B' : '#9CA3AF' }}>{selectedAxis.open_e}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span className="text-muted">Закрытых:</span>
                  <span style={{ fontWeight: 600, color: '#10B981' }}>{selectedAxis.closed}</span>
                </div>
              </div>
              <button type="button" onClick={() => setSelectedSection(null)} style={{ marginTop: 12, fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer' }}>Снять выбор</button>
            </div>
          ) : (
            <div>
              <p className="text-muted" style={{ fontSize: 12, marginBottom: 12 }}>
                Кликните на сектор для детализации.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {axes.filter((a) => a.open_s > 0).sort((a, b) => b.open_s - a.open_s).slice(0, 5).map((a) => (
                  <button
                    key={a.code}
                    type="button"
                    onClick={() => setSelectedSection(a.code)}
                    style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #FECACA', background: '#FEF2F2', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', fontSize: 12, alignItems: 'center' }}
                  >
                    <span style={{ fontWeight: 600, color: '#B91C1C' }}>{a.code}</span>
                    <span style={{ color: '#B91C1C' }}>{a.open_s} блокирующих</span>
                  </button>
                ))}
                {axes.filter((a) => !a.hasDoc).slice(0, 5).map((a) => (
                  <button
                    key={a.code}
                    type="button"
                    onClick={() => setSelectedSection(a.code)}
                    style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #E5E7EB', background: '#F9FAFB', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', fontSize: 12, alignItems: 'center' }}
                  >
                    <span style={{ fontWeight: 500, color: '#6B7280' }}>{a.code}</span>
                    <span style={{ color: '#9CA3AF' }}>нет документа</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="text-muted" style={{ marginTop: 12, fontSize: 11 }}>
        Основа: все {PP87_SECTIONS.length} разделов ПП87. Слой 1 (зелёный) — документ загружен. Слой 2 (контур) — замечания. Слой 3 (размер) — число открытых замечаний.
      </p>
    </div>
  );
}

// _RadarView_LEGACY uses the RadarAxis type defined above

function _RadarView_LEGACY({ remarks }: { remarks: Remark[] }) {
  // Legacy version kept for reference — not rendered
  const axes = useMemo<RadarAxis[]>(() => {
    if (!remarks.length) return [];
    const byKey: Record<string, { total: number; closed: number }> = {};
    for (const r of remarks) {
      const key = (r.specialist_role || r.aspect || r.section_code || '').trim();
      if (!key) continue;
      const isClosed = (r.remark_status || (r.closed_at ? 'closed' : 'open')) === 'closed';
      byKey[key] ||= { total: 0, closed: 0 };
      byKey[key].total += 1;
      if (isClosed) byKey[key].closed += 1;
    }
    return Object.entries(byKey).map(([name, v]) => ({
      name, total: v.total, closed: v.closed, value: v.total > 0 ? v.closed / v.total : 0,
    })).sort((a, b) => b.total - a.total).slice(0, 12);
  }, [remarks]);
  void axes;
  return null;
}
void _RadarView_LEGACY;


/* ─── Graph ─────────────────────────────────────────────────────────────── */
type KGNode = { id: string; label: string; type: string };
type KGEdge = { source: string; target: string; type: string };
type KGResponse = { nodes: KGNode[]; edges: KGEdge[]; note?: string };

function GraphView({ projectId, snapshotId, remarks }: { projectId: string; snapshotId: string | null; remarks: Remark[] }) {
  const [tab, setTab] = useState<'norms' | 'kg'>('norms');

  const { data, isLoading, error } = useQuery<KGResponse>({
    queryKey: ['kg-subgraph', projectId, snapshotId],
    queryFn: () => api.get<KGResponse>(`/projects/${projectId}/snapshots/${snapshotId}/kg/subgraph?limit=80`),
    enabled: !!projectId && !!snapshotId && tab === 'kg',
  });

  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
      {/* Tab bar */}
      <div className="flex items-center" style={{ gap: 8, marginBottom: 20 }}>
        {(['norms', 'kg'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            style={{
              padding: '6px 14px', fontSize: 12, fontWeight: tab === t ? 600 : 400,
              borderRadius: 20, border: tab === t ? '1px solid #0A1628' : '1px solid #E5E7EB',
              background: tab === t ? '#0A1628' : 'transparent',
              color: tab === t ? '#FFFFFF' : '#6B7280', cursor: 'pointer',
            }}
          >
            {t === 'norms' ? 'Замечания · Разделы · Нормы' : 'Граф знаний (KG)'}
          </button>
        ))}
      </div>

      {tab === 'norms' && <NormDepsGraph remarks={remarks} />}

      {tab === 'kg' && !snapshotId && (
        <EmptyState icon={GitBranch} title="Нет активной редакции"
          description="Создайте редакцию проекта, чтобы увидеть граф знаний." />
      )}
      {tab === 'kg' && snapshotId && isLoading && (
        <div className="text-muted text-center" style={{ padding: 32, fontSize: 13 }}>Загружаю граф знаний…</div>
      )}
      {tab === 'kg' && snapshotId && !isLoading && (error || !data) && (
        <EmptyState icon={GitBranch} title="Не удалось загрузить граф"
          description="Возможно, Neo4j недоступен или KG не построен. Откройте вкладку «Документы» → Build KG." />
      )}
      {tab === 'kg' && snapshotId && !isLoading && data && data.nodes.length === 0 && (
        <EmptyState icon={GitBranch} title="Граф знаний пуст"
          description={data.note ?? 'Загрузите документы и запустите Build KG.'} />
      )}
      {tab === 'kg' && snapshotId && !isLoading && data && data.nodes.length > 0 && (
        <KGForceGraph nodes={data.nodes} edges={data.edges} />
      )}
    </div>
  );
}

/* ─── Normative dependency graph ─────────────────────────────────────────── */
function NormDepsGraph({ remarks }: { remarks: Remark[] }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<'all' | 'open' | 's-defect'>('all');
  const svgRef = useRef<SVGSVGElement>(null);
  const width = 720;
  const height = 460;

  // Build section → remarks map
  const filtered = useMemo(() => {
    return remarks.filter((r) => {
      if (filterType === 'open') return (r.remark_status || (r.closed_at ? 'closed' : 'open')) !== 'closed';
      if (filterType === 's-defect') return r.defect_type === 'S-defect' && (r.remark_status || (r.closed_at ? 'closed' : 'open')) !== 'closed';
      return true;
    });
  }, [remarks, filterType]);

  type NormNode = d3.SimulationNodeDatum & { id: string; label: string; kind: 'section' | 'remark' | 'norm'; open_s: number; open_e: number; closed: number; defect_type?: string | null };
  type NormLink = d3.SimulationLinkDatum<NormNode> & { defect_type: string | null; link_kind: 'sec-rmk' | 'rmk-norm' };

  useEffect(() => {
    if (!svgRef.current) return;
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Build section map
    const sectionMap: Record<string, { open_s: number; open_e: number; closed: number }> = {};
    for (const r of filtered) {
      const sec = (r.section_code || 'Без раздела').toUpperCase().split(/[-./]/)[0].trim() || 'Без раздела';
      const isClosed = (r.remark_status || (r.closed_at ? 'closed' : 'open')) === 'closed';
      sectionMap[sec] = sectionMap[sec] || { open_s: 0, open_e: 0, closed: 0 };
      if (isClosed) sectionMap[sec].closed++;
      else if (r.defect_type === 'S-defect') sectionMap[sec].open_s++;
      else sectionMap[sec].open_e++;
    }

    const secNodes: NormNode[] = Object.entries(sectionMap).map(([code, s]) => ({
      id: `sec:${code}`, label: code, kind: 'section', ...s,
    }));

    const remarkNodes: NormNode[] = filtered.map((r) => ({
      id: `rmk:${r.id}`, label: (r.body || '').slice(0, 28) + ((r.body || '').length > 28 ? '…' : ''),
      kind: 'remark', open_s: r.defect_type === 'S-defect' ? 1 : 0, open_e: r.defect_type === 'E-gap' ? 1 : 0, closed: 0,
      defect_type: r.defect_type,
    }));

    // Build norm nodes from cited_norms
    const normSet = new Map<string, { count: number }>();
    for (const r of filtered) {
      for (const n of (r.cited_norms || [])) {
        const key = n.trim();
        if (!key) continue;
        normSet.set(key, { count: (normSet.get(key)?.count ?? 0) + 1 });
      }
    }
    const normNodes: NormNode[] = Array.from(normSet.entries()).map(([label]) => ({
      id: `norm:${label}`, label: label.slice(0, 22) + (label.length > 22 ? '…' : ''),
      kind: 'norm', open_s: 0, open_e: 0, closed: 0,
    }));

    const links: NormLink[] = [
      ...filtered.map((r) => {
        const sec = (r.section_code || 'Без раздела').toUpperCase().split(/[-./]/)[0].trim() || 'Без раздела';
        return { source: `sec:${sec}`, target: `rmk:${r.id}`, defect_type: r.defect_type ?? null, link_kind: 'sec-rmk' as const };
      }),
      ...filtered.flatMap((r) =>
        (r.cited_norms || []).map((n) => ({
          source: `rmk:${r.id}`, target: `norm:${n.trim()}`,
          defect_type: r.defect_type ?? null, link_kind: 'rmk-norm' as const,
        }))
      ),
    ];

    const allNodes = [...secNodes, ...remarkNodes, ...normNodes];

    const sectionColor = (n: NormNode) =>
      n.open_s > 0 ? '#EF4444' : n.open_e > 0 ? '#F59E0B' : n.closed > 0 ? '#10B981' : '#9CA3AF';

    const remarkColor = (n: NormNode) =>
      n.defect_type === 'S-defect' ? '#FCA5A5' : n.defect_type === 'E-gap' ? '#FDE68A' : '#D1D5DB';

    const link = svg.append('g').attr('stroke-opacity', 0.35)
      .selectAll('line').data(links).enter().append('line')
      .attr('stroke', (d) => d.link_kind === 'rmk-norm' ? '#A78BFA' : d.defect_type === 'S-defect' ? '#EF4444' : d.defect_type === 'E-gap' ? '#F59E0B' : '#9CA3AF')
      .attr('stroke-width', (d) => d.link_kind === 'rmk-norm' ? 1 : d.defect_type === 'S-defect' ? 1.5 : 1)
      .attr('stroke-dasharray', (d) => d.link_kind === 'rmk-norm' ? '3,3' : 'none');

    const node = svg.append('g').selectAll('g').data(allNodes).enter().append('g')
      .style('cursor', 'pointer')
      .on('click', (_, d) => setSelected((prev) => (prev === d.id ? null : d.id)));

    // Norm nodes as diamonds (rotated rect)
    node.filter((d) => d.kind === 'norm').append('rect')
      .attr('width', 14).attr('height', 14)
      .attr('x', -7).attr('y', -7)
      .attr('transform', 'rotate(45)')
      .attr('fill', '#EDE9FE')
      .attr('stroke', '#7C3AED')
      .attr('stroke-width', 1.5);

    node.filter((d) => d.kind === 'norm').append('text')
      .text((d) => d.label.slice(0, 10))
      .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
      .attr('dy', 16).attr('font-size', 9).attr('fill', '#6D28D9').attr('pointer-events', 'none');

    // Section + remark circles
    node.filter((d) => d.kind !== 'norm').append('circle')
      .attr('r', (d) => d.kind === 'section' ? Math.max(12, 8 + (d.open_s + d.open_e + d.closed) * 1.5) : 4)
      .attr('fill', (d) => d.kind === 'section' ? sectionColor(d) : remarkColor(d))
      .attr('stroke', (d) => d.kind === 'section' ? '#FFFFFF' : 'none')
      .attr('stroke-width', 2)
      .attr('opacity', (d) => (selected && d.id !== selected && !links.find((l) => (l.source as NormNode).id === selected && (l.target as NormNode).id === d.id || (l.source as NormNode).id === d.id && (l.target as NormNode).id === selected)) ? 0.35 : 1);

    node.filter((d) => d.kind === 'section').append('text')
      .text((d) => d.label)
      .attr('text-anchor', 'middle').attr('dominant-baseline', 'middle')
      .attr('font-size', 10).attr('font-weight', 700).attr('fill', '#FFFFFF').attr('pointer-events', 'none');

    node.append('title').text((d) => d.kind === 'section'
      ? `${d.label}\nБлокирующих: ${d.open_s}, Устранимых: ${d.open_e}, Закрытых: ${d.closed}`
      : d.kind === 'norm' ? `Норма: ${d.label}` : d.label);

    const sim = d3.forceSimulation(allNodes)
      .force('link', d3.forceLink<NormNode, NormLink>(links).id((d) => d.id).distance((d) => {
        if ((d as NormLink).link_kind === 'rmk-norm') return 40;
        const src = d.source as NormNode;
        return 20 + Math.max(12, 8 + (src.open_s + src.open_e + src.closed) * 1.5);
      }))
      .force('charge', d3.forceManyBody().strength((d) => (d as NormNode).kind === 'section' ? -300 : (d as NormNode).kind === 'norm' ? -60 : -20))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius((d) => (d as NormNode).kind === 'section' ? 28 : (d as NormNode).kind === 'norm' ? 14 : 6))
      .on('tick', () => {
        link.attr('x1', (d) => (d.source as NormNode).x ?? 0).attr('y1', (d) => (d.source as NormNode).y ?? 0)
            .attr('x2', (d) => (d.target as NormNode).x ?? 0).attr('y2', (d) => (d.target as NormNode).y ?? 0);
        node.attr('transform', (d) => `translate(${Math.max(20, Math.min(width - 20, d.x ?? width / 2))},${Math.max(20, Math.min(height - 20, d.y ?? height / 2))})`);
      });

    const drag = d3.drag<SVGGElement, NormNode>()
      .on('start', (ev, d) => { if (!ev.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
      .on('drag', (ev, d) => { d.fx = ev.x; d.fy = ev.y; })
      .on('end', (ev, d) => { if (!ev.active) sim.alphaTarget(0); d.fx = null; d.fy = null; });
    node.call(drag);

    return () => { sim.stop(); };
  }, [filtered, selected]);

  const selectedSection = selected?.startsWith('sec:') ? selected.slice(4) : null;
  const selectedRemarks = selectedSection
    ? filtered.filter((r) => (r.section_code || 'Без раздела').toUpperCase().split(/[-./]/)[0].trim() === selectedSection)
    : [];

  if (filtered.length === 0 && remarks.length === 0) {
    return (
      <EmptyState icon={GitMerge} title="Замечаний нет"
        description="Граф нормативных связей появится после добавления замечаний к проекту." />
    );
  }

  return (
    <div>
      <div className="flex items-center" style={{ gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <div className="flex items-center" style={{ gap: 6, fontSize: 12 }}>
          {(['all', 'open', 's-defect'] as const).map((f) => (
            <button key={f} type="button" onClick={() => setFilterType(f)}
              style={{
                padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: filterType === f ? 600 : 400,
                border: filterType === f ? '1px solid #0A1628' : '1px solid #E5E7EB',
                background: filterType === f ? '#0A1628' : 'transparent',
                color: filterType === f ? '#FFF' : '#6B7280', cursor: 'pointer',
              }}
            >
              {f === 'all' ? 'Все' : f === 'open' ? 'Открытые' : 'Блокирующие'}
            </button>
          ))}
        </div>
        <span className="text-muted" style={{ fontSize: 11, marginLeft: 'auto' }}>
          {Object.keys(filtered.reduce<Record<string, number>>((acc, r) => { const s = (r.section_code || '').split(/[-./]/)[0]; acc[s] = 1; return acc; }, {})).length} разделов · {filtered.length} замечаний
        </span>
        <div className="flex items-center" style={{ gap: 10, fontSize: 11, flexWrap: 'wrap' }}>
          <span className="flex items-center" style={{ gap: 3 }}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#EF4444', display: 'inline-block' }} /> Блокирующие</span>
          <span className="flex items-center" style={{ gap: 3 }}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#F59E0B', display: 'inline-block' }} /> Устранимые</span>
          <span className="flex items-center" style={{ gap: 3 }}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#10B981', display: 'inline-block' }} /> Закрытые</span>
          <span className="flex items-center" style={{ gap: 3 }}><span style={{ width: 9, height: 9, background: '#EDE9FE', border: '1.5px solid #7C3AED', transform: 'rotate(45deg)', display: 'inline-block' }} /> Норма</span>
        </div>
      </div>

      <div className="flex" style={{ gap: 16, alignItems: 'flex-start' }}>
        <svg ref={svgRef} width={width} height={height} style={{ display: 'block', border: '1px solid #F3F4F6', borderRadius: 8, background: '#FAFAFA', maxWidth: '100%' }} />
        {selectedSection && (
          <div style={{ width: 220, flexShrink: 0, padding: 14, borderRadius: 8, border: '1px solid #E5E7EB', background: '#F9FAFB' }}>
            <h5 style={{ fontSize: 13, fontWeight: 700, margin: '0 0 8px' }}>{selectedSection}</h5>
            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 8 }}>{selectedRemarks.length} замечаний</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 320, overflowY: 'auto' }}>
              {selectedRemarks.slice(0, 20).map((r) => (
                <div key={r.id} style={{ padding: '5px 8px', borderRadius: 5, background: '#FFF', border: '1px solid #E5E7EB', fontSize: 11, lineHeight: 1.45 }}>
                  <span style={{ fontWeight: 600, color: r.defect_type === 'S-defect' ? '#B91C1C' : '#92400E', marginRight: 4 }}>
                    {r.defect_type === 'S-defect' ? 'С' : 'Е'}
                  </span>
                  {(r.body || '').slice(0, 80)}{(r.body || '').length > 80 ? '…' : ''}
                </div>
              ))}
            </div>
            <button type="button" onClick={() => setSelected(null)} style={{ marginTop: 8, fontSize: 10, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer' }}>Сбросить</button>
          </div>
        )}
      </div>
    </div>
  );
}

function KGForceGraph({ nodes, edges }: { nodes: KGNode[]; edges: KGEdge[] }) {
  const ref = useRef<SVGSVGElement>(null);
  const width = 760;
  const height = 480;

  useEffect(() => {
    if (!ref.current) return;
    const svg = d3.select(ref.current);
    svg.selectAll('*').remove();

    type SimNode = d3.SimulationNodeDatum & KGNode;
    type SimLink = d3.SimulationLinkDatum<SimNode> & { type: string };

    const simNodes: SimNode[] = nodes.map((n) => ({ ...n }));
    const simLinks: SimLink[] = edges
      .filter((e) => simNodes.find((n) => n.id === e.source) && simNodes.find((n) => n.id === e.target))
      .map((e) => ({ source: e.source, target: e.target, type: e.type }));

    const colorFor = (t: string) =>
      t === 'DocumentVersion' ? '#6366F1' :
      t === 'Section' ? '#10B981' :
      t === 'ExtractedEntity' ? '#F59E0B' : '#9CA3AF';

    const link = svg.append('g')
      .attr('stroke', '#CBD5E1')
      .attr('stroke-opacity', 0.6)
      .selectAll('line')
      .data(simLinks)
      .enter().append('line')
      .attr('stroke-width', 1);

    const node = svg.append('g')
      .selectAll('g')
      .data(simNodes)
      .enter().append('g')
      .style('cursor', 'grab');

    node.append('circle')
      .attr('r', (d) => d.type === 'DocumentVersion' ? 9 : d.type === 'Section' ? 6 : 4)
      .attr('fill', (d) => colorFor(d.type))
      .attr('stroke', '#FFFFFF')
      .attr('stroke-width', 1.5);

    node.append('title').text((d) => `${d.label}\n(${d.type})`);

    node.append('text')
      .text((d) => d.label.length > 24 ? `${d.label.slice(0, 23)}…` : d.label)
      .attr('font-size', 10)
      .attr('fill', '#0A1628')
      .attr('dx', 10)
      .attr('dy', 4);

    const sim = d3.forceSimulation(simNodes)
      .force('link', d3.forceLink<SimNode, SimLink>(simLinks).id((d) => d.id).distance(60))
      .force('charge', d3.forceManyBody().strength(-180))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(14))
      .on('tick', () => {
        link
          .attr('x1', (d) => (d.source as SimNode).x ?? 0)
          .attr('y1', (d) => (d.source as SimNode).y ?? 0)
          .attr('x2', (d) => (d.target as SimNode).x ?? 0)
          .attr('y2', (d) => (d.target as SimNode).y ?? 0);
        node.attr('transform', (d) => `translate(${d.x ?? 0},${d.y ?? 0})`);
      });

    const drag = d3.drag<SVGGElement, SimNode>()
      .on('start', (event, d) => { if (!event.active) sim.alphaTarget(0.3).restart(); d.fx = d.x; d.fy = d.y; })
      .on('drag', (event, d) => { d.fx = event.x; d.fy = event.y; })
      .on('end', (event, d) => { if (!event.active) sim.alphaTarget(0); d.fx = null; d.fy = null; });
    node.call(drag);

    return () => { sim.stop(); };
  }, [nodes, edges]);

  return (
    <div>
      <div className="flex items-center" style={{ gap: 16, marginBottom: 8 }}>
        <div className="flex items-center text-muted" style={{ gap: 12, fontSize: 11 }}>
          <Legend color="#6366F1" label="Версия документа" />
          <Legend color="#10B981" label="Раздел" />
          <Legend color="#F59E0B" label="Сущность" />
        </div>
        <span className="text-muted" style={{ marginLeft: 'auto', fontSize: 11 }}>
          узлов: {nodes.length}, рёбер: {edges.length}
        </span>
      </div>
      <svg ref={ref} width={width} height={height} style={{ display: 'block', maxWidth: '100%', border: '1px solid #F3F4F6', borderRadius: 8, background: '#FAFAFA' }} />
    </div>
  );
}

function Legend({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center" style={{ gap: 4 }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
      {label}
    </span>
  );
}

/* ─── Conflicts ──────────────────────────────────────────────────────────── */
function ConflictsView({ remarks, projectId }: { remarks: Remark[]; projectId: string }) {
  // Real conflict = open remark + at least one response that is not accepted
  const [reviewState, setReviewState] = useState<Record<string, 'accepting' | 'rejecting' | 'done_accept' | 'done_reject'>>({});

  const conflicts = useMemo(() => {
    return remarks
      .filter((r) => {
        const isOpen = (r.remark_status || (r.closed_at ? 'closed' : 'open')) !== 'closed';
        const hasPendingResponse = (r.responses || []).some((resp) => (resp.status || 'pending') !== 'accepted');
        return isOpen && hasPendingResponse;
      })
      .sort((a, b) => sevWeight(b) - sevWeight(a))
      .slice(0, 10);
  }, [remarks]);

  const reviewResponse = async (remarkId: string, responseId: string, action: 'accept' | 'reject') => {
    const key = `${remarkId}:${responseId}`;
    setReviewState((p) => ({ ...p, [key]: action === 'accept' ? 'accepting' : 'rejecting' }));
    try {
      await api.post(`/projects/${projectId}/remarks/${remarkId}/responses/${responseId}/${action}`, {});
      setReviewState((p) => ({ ...p, [key]: action === 'accept' ? 'done_accept' : 'done_reject' }));
    } catch {
      setReviewState((p) => { const n = { ...p }; delete n[key]; return n; });
    }
  };

  const DEFECT_COLOR: Record<string, string> = { 'S-defect': '#EF4444', 'E-gap': '#F59E0B' };
  const SEV_COLOR_C: Record<string, string> = { critical: '#EF4444', high: '#F59E0B', medium: '#6B7280', low: '#9CA3AF' };

  return (
    <div>
      <div className="flex items-start rounded-card" style={{ gap: 12, padding: 16, background: 'rgba(245,158,11,.05)', border: '1px solid rgba(245,158,11,.3)', marginBottom: 20 }}>
        <AlertTriangle size={18} stroke="#F59E0B" />
        <div className="text-ink" style={{ flex: 1, fontSize: 13, lineHeight: 1.5 }}>
          Конфликт — замечание, на которое проектировщик дал ответ, но куратор его ещё не рассмотрел
          (статус ответа: на рассмотрении). Куратор принимает ответ (замечание закрывается) или
          отклоняет (замечание остаётся открытым). Если открытых замечаний много, но конфликтов нет —
          это значит, что проектировщик ещё не ответил ни на одно.
        </div>
      </div>
      {conflicts.length === 0 ? (
        <p className="text-muted text-center" style={{ padding: '32px 0', fontSize: 14 }}>Конфликтов не обнаружено — все ответы приняты или замечания закрыты</p>
      ) : conflicts.map((r) => {
        const pendingResponses = (r.responses || []).filter((resp) => (resp.status || 'pending') !== 'accepted');
        const defectColor = DEFECT_COLOR[r.defect_type || ''] || '#6B7280';
        const sevColor = SEV_COLOR_C[r.severity || ''] || '#6B7280';
        return (
          <div key={r.id} className="rounded-card mb-3" style={{ padding: 20, background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
            {/* Remark header */}
            <div className="flex items-start" style={{ gap: 12, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <div className="flex items-center" style={{ gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: sevColor }}>{r.severity}</span>
                  <span className="rounded-pill" style={{ padding: '1px 8px', fontSize: 11, fontWeight: 600, background: `${defectColor}18`, color: defectColor }}>
                    {r.defect_type === 'E-gap' ? 'Устранимое' : 'Блокирующее'}
                  </span>
                  <span className="text-muted" style={{ fontSize: 11 }}>{r.section_code || 'без раздела'}</span>
                  <span className="text-muted tabular" style={{ fontSize: 11, marginLeft: 'auto' }}>{r.id.slice(0, 8)}</span>
                </div>
                <p className="text-ink" style={{ fontSize: 13, lineHeight: 1.5, margin: 0 }}>{trunc(r.body, 200)}</p>
              </div>
            </div>

            {/* Pending responses */}
            <div style={{ borderTop: '1px solid #F3F4F6', paddingTop: 12 }}>
              <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 8 }}>
                Ответы проектировщика ({pendingResponses.length})
              </div>
              {pendingResponses.map((resp) => {
                const key = `${r.id}:${resp.id}`;
                const state = reviewState[key];
                const isDone = state === 'done_accept' || state === 'done_reject';
                const isLoading = state === 'accepting' || state === 'rejecting';
                return (
                  <div key={resp.id} className="rounded-card mb-2" style={{ padding: '10px 12px', background: '#F9FAFB', border: isDone ? '1px solid #D1FAE5' : '1px solid #E5E7EB' }}>
                    <div className="flex items-start" style={{ gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        {resp.status === 'rejected' && (
                          <span className="rounded-pill" style={{ display: 'inline-block', marginBottom: 4, padding: '1px 8px', fontSize: 10, fontWeight: 700, background: 'rgba(239,68,68,.1)', color: '#EF4444' }}>
                            Отклонён
                          </span>
                        )}
                        <p className="text-ink" style={{ fontSize: 13, lineHeight: 1.45, margin: 0 }}>{trunc(resp.body, 300)}</p>
                        {resp.created_at && (
                          <span className="text-muted" style={{ fontSize: 11 }}>{new Date(resp.created_at).toLocaleDateString('ru-RU')}</span>
                        )}
                      </div>
                      {!isDone && (
                        <div className="flex" style={{ gap: 6, flexShrink: 0 }}>
                          <button
                            type="button"
                            disabled={isLoading}
                            onClick={() => reviewResponse(r.id, resp.id, 'accept')}
                            style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, background: '#10B981', color: '#fff', border: 'none', borderRadius: 6, cursor: isLoading ? 'wait' : 'pointer', opacity: isLoading ? 0.7 : 1 }}
                          >
                            Принять
                          </button>
                          <button
                            type="button"
                            disabled={isLoading}
                            onClick={() => reviewResponse(r.id, resp.id, 'reject')}
                            style={{ padding: '5px 12px', fontSize: 12, fontWeight: 600, background: 'transparent', color: '#EF4444', border: '1px solid #EF4444', borderRadius: 6, cursor: isLoading ? 'wait' : 'pointer', opacity: isLoading ? 0.7 : 1 }}
                          >
                            Отклонить
                          </button>
                        </div>
                      )}
                      {state === 'done_accept' && <span style={{ fontSize: 12, fontWeight: 600, color: '#10B981' }}>✓ Принят</span>}
                      {state === 'done_reject' && <span style={{ fontSize: 12, fontWeight: 600, color: '#EF4444' }}>✗ Отклонён</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Cascade ────────────────────────────────────────────────────────────── */
type ImpactResult = { affects_sections: string[]; related_remarks: Array<{ id: string; body: string; section_code?: string | null; defect_type?: string | null; severity?: string | null }> };

function CascadeView({ remarks, projectId }: { remarks: Remark[]; projectId: string }) {
  const openRemarks = useMemo(
    () => remarks.filter((r) => (r.remark_status || (r.closed_at ? 'closed' : 'open')) !== 'closed').slice(0, 10),
    [remarks],
  );
  const [loading, setLoading] = useState<Record<string, boolean>>({});
  const [impacts, setImpacts] = useState<Record<string, ImpactResult>>({});

  const computeImpact = async (remarkId: string) => {
    setLoading((p) => ({ ...p, [remarkId]: true }));
    try {
      const res = await api.post<ImpactResult>(`/projects/${projectId}/remarks/${remarkId}/impact`, {});
      setImpacts((p) => ({ ...p, [remarkId]: res }));
    } catch {
      // ignore
    } finally {
      setLoading((p) => ({ ...p, [remarkId]: false }));
    }
  };

  // Pre-fill impacts from existing affects_sections data
  useEffect(() => {
    const initial: Record<string, ImpactResult> = {};
    for (const r of openRemarks) {
      if (r.affects_sections && r.affects_sections.length > 0) {
        const relatedInSections = openRemarks.filter(
          (x) => x.id !== r.id && r.affects_sections!.includes(x.section_code || ''),
        );
        initial[r.id] = {
          affects_sections: r.affects_sections,
          related_remarks: relatedInSections.map((x) => ({
            id: x.id, body: x.body || '', section_code: x.section_code, defect_type: x.defect_type, severity: x.severity,
          })),
        };
      }
    }
    setImpacts((p) => ({ ...initial, ...p }));
  }, [openRemarks]);

  const DEFECT_LABEL: Record<string, string> = { 'S-defect': 'Блокирующее', 'E-gap': 'Устранимое' };
  const SEV_COLOR: Record<string, string> = { critical: '#EF4444', high: '#F59E0B', medium: '#6B7280', low: '#9CA3AF' };

  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
      <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>Зона влияния</h4>
      <p className="text-muted mt-1" style={{ fontSize: 13 }}>
        Какие разделы ПД потребуют изменений и какие замечания связаны при исправлении каждого.
      </p>
      <div className="mt-4">
        {openRemarks.length === 0 ? (
          <p className="text-muted text-center" style={{ padding: '24px 0', fontSize: 14 }}>Нет открытых замечаний</p>
        ) : openRemarks.map((r) => {
          const impact = impacts[r.id];
          const isLoading = loading[r.id];
          return (
            <div key={r.id} className="rounded-card mb-3" style={{ border: '1px solid #E5E7EB' }}>
              {/* Header */}
              <div className="flex items-center" style={{ padding: '12px 14px', borderBottom: impact ? '1px solid #F3F4F6' : 'none', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <span className="text-ink" style={{ fontSize: 13, fontWeight: 600 }}>
                    {r.section_code || 'без раздела'} · {r.id.slice(0, 8)}
                  </span>
                  <p className="text-muted mt-0.5" style={{ fontSize: 12, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {r.body}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => computeImpact(r.id)}
                  disabled={isLoading}
                  style={{
                    flexShrink: 0, padding: '6px 12px', fontSize: 12, fontWeight: 600,
                    background: '#F59E0B', color: '#fff', border: 'none', borderRadius: 6, cursor: isLoading ? 'wait' : 'pointer',
                    opacity: isLoading ? 0.7 : 1,
                  }}
                >
                  {isLoading ? '…' : impact ? 'Обновить' : 'Определить зону'}
                </button>
              </div>

              {/* Two blocks */}
              {impact && (
                <div className="grid grid-cols-2" style={{ gap: 0 }}>
                  {/* Block 1: Affected sections */}
                  <div style={{ padding: '12px 14px', borderRight: '1px solid #F3F4F6' }}>
                    <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 8 }}>
                      Затронутые разделы ПД
                    </div>
                    {impact.affects_sections.length === 0 ? (
                      <span className="text-muted" style={{ fontSize: 12 }}>Других разделов не затрагивает</span>
                    ) : (
                      <div className="flex" style={{ flexWrap: 'wrap', gap: 6 }}>
                        {impact.affects_sections.map((sec) => (
                          <span key={sec} className="rounded-pill" style={{ padding: '3px 10px', fontSize: 12, fontWeight: 600, background: 'rgba(245,158,11,.1)', color: '#92400E' }}>
                            {sec}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Block 2: Related remarks */}
                  <div style={{ padding: '12px 14px' }}>
                    <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', marginBottom: 8 }}>
                      Связанные замечания
                    </div>
                    {impact.related_remarks.length === 0 ? (
                      <span className="text-muted" style={{ fontSize: 12 }}>Связанных замечаний нет</span>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {impact.related_remarks.slice(0, 5).map((rel) => (
                          <div key={rel.id} className="rounded-card" style={{ padding: '6px 10px', background: '#F9FAFB', border: '1px solid #E5E7EB' }}>
                            <div className="flex items-center" style={{ gap: 6, marginBottom: 2 }}>
                              <span style={{ fontSize: 10, fontWeight: 700, color: SEV_COLOR[rel.severity || ''] || '#6B7280', textTransform: 'uppercase' }}>{rel.severity}</span>
                              <span className="text-muted" style={{ fontSize: 10 }}>·</span>
                              <span style={{ fontSize: 10, fontWeight: 600, color: '#6B7280' }}>{DEFECT_LABEL[rel.defect_type || ''] || rel.defect_type}</span>
                              <span className="text-muted" style={{ fontSize: 10 }}>· {rel.section_code}</span>
                            </div>
                            <p className="text-ink" style={{ fontSize: 12, lineHeight: 1.4, margin: 0, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {rel.body}
                            </p>
                          </div>
                        ))}
                        {impact.related_remarks.length > 5 && (
                          <span className="text-muted" style={{ fontSize: 11 }}>+{impact.related_remarks.length - 5} ещё</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Waves (parity между редакциями) ───────────────────────────────────── */
type DiffDoc = { document_id: string; title: string; from_version_seq?: number; to_version_seq?: number };
type DiffResponse = {
  from_snapshot_id: string;
  to_snapshot_id: string;
  documents: {
    added: DiffDoc[];
    removed: DiffDoc[];
    modified: DiffDoc[];
    unchanged_count: number;
  };
  remarks: {
    from: { total: number; open: number; closed: number };
    to: { total: number; open: number; closed: number };
  };
};

export function WavesView({ projectId, snapshots }: { projectId: string; snapshots: Snapshot[] }) {
  // 11.1 (2026-07-05): предыдущая редакция ВСЕГДА инвалидируется при загрузке
  // новой — это нормальная история, а не «неудачная попытка» (мусорные снапшоты
  // больше не плодятся: rebuild-in-place при досыле, реактивация при реингесте).
  // Поэтому архивные участвуют в сравнении по умолчанию — иначе сравнение
  // никогда не оживает (активных всегда 1). Чекбокс остаётся, чтобы скрыть.
  const [withInvalidated, setWithInvalidated] = useState(true);
  // Сортируем по дате создания: старые сверху, новые внизу
  const sorted = useMemo(() => {
    return [...snapshots]
      .filter((s) => withInvalidated || !s.invalidated_at)
      .sort((a, b) => {
      const ta = a.created_at ? new Date(a.created_at).getTime() : 0;
      const tb = b.created_at ? new Date(b.created_at).getTime() : 0;
      return ta - tb;
    });
  }, [snapshots, withInvalidated]);

  const defaultFrom = sorted.length >= 2 ? sorted[sorted.length - 2].id : '';
  const defaultTo = sorted.length >= 1 ? sorted[sorted.length - 1].id : '';
  const [fromId, setFromId] = useState<string>(defaultFrom);
  const [toId, setToId] = useState<string>(defaultTo);

  useEffect(() => {
    // Пустой ИЛИ отфильтрованный (аннулированная скрыта) выбор → к дефолту.
    const ids = new Set(sorted.map((s) => s.id));
    if ((!fromId || !ids.has(fromId)) && defaultFrom) setFromId(defaultFrom);
    if ((!toId || !ids.has(toId)) && defaultTo) setToId(defaultTo);
  }, [defaultFrom, defaultTo, fromId, toId, sorted]);

  const enabled = !!projectId && !!fromId && !!toId && fromId !== toId;
  const { data, isLoading, error } = useQuery<DiffResponse>({
    queryKey: ['snapshot-diff', projectId, fromId, toId],
    queryFn: () => api.get<DiffResponse>(`/projects/${projectId}/snapshots/${fromId}/diff?to=${toId}`),
    enabled,
  });

  if (sorted.length <= 1) {
    return (
      <div>
        <EmptyState
          icon={GitBranch}
          title="Сравнение появится после загрузки второй редакции"
          description="Сравнение редакций показывает, какие документы добавились, изменились или удалились между двумя редакциями проекта, и как изменились замечания."
        />
        {snapshots.some((s) => s.invalidated_at) && (
          <p className="text-muted text-center" style={{ fontSize: 12, marginTop: 8 }}>
            <label style={{ cursor: 'pointer', userSelect: 'none' }}>
              <input type="checkbox" checked={withInvalidated}
                     onChange={(e) => setWithInvalidated(e.target.checked)}
                     style={{ marginRight: 6, verticalAlign: 'middle' }} />
              показать аннулированные редакции (неудачные попытки загрузки)
            </label>
          </p>
        )}
      </div>
    );
  }

  const fmtDate = (iso?: string) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleDateString('ru-RU', { day: '2-digit', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  const optLabel = (s: Snapshot, i: number) =>
    `Редакция №${i + 1} · ${fmtDate(s.created_at)}${s.invalidated_at ? ' (аннулирована)' : ''}`;

  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24 }}>
      <div className="flex items-center" style={{ gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>Сравнение редакций</h4>
        <div className="flex items-center" style={{ gap: 8, marginLeft: 'auto' }}>
          {snapshots.some((s) => s.invalidated_at) && (
            <label className="text-muted" style={{ fontSize: 11.5, cursor: 'pointer', userSelect: 'none', marginRight: 8 }}>
              <input type="checkbox" checked={withInvalidated}
                     onChange={(e) => setWithInvalidated(e.target.checked)}
                     style={{ marginRight: 5, verticalAlign: 'middle' }} />
              с аннулированными
            </label>
          )}
          <label className="text-muted" style={{ fontSize: 12 }}>От:</label>
          <select value={fromId} onChange={(e) => setFromId(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13 }}>
            {sorted.map((s, i) => <option key={s.id} value={s.id}>{optLabel(s, i)}</option>)}
          </select>
          <label className="text-muted" style={{ fontSize: 12 }}>До:</label>
          <select value={toId} onChange={(e) => setToId(e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #E5E7EB', fontSize: 13 }}>
            {sorted.map((s, i) => <option key={s.id} value={s.id}>{optLabel(s, i)}</option>)}
          </select>
        </div>
      </div>

      {fromId === toId && (
        <p className="text-muted" style={{ fontSize: 13 }}>Выберите две разные редакции.</p>
      )}
      {fromId !== toId && isLoading && (
        <p className="text-muted text-center" style={{ padding: 32, fontSize: 13 }}>Считаю различия…</p>
      )}
      {fromId !== toId && error && (
        <p className="text-muted text-center" style={{ padding: 32, fontSize: 13 }}>Не удалось загрузить сравнение — проверьте, что обе редакции существуют.</p>
      )}
      {fromId !== toId && data && (
        <div>
          <div className="grid grid-cols-4" style={{ gap: 12, marginBottom: 20 }}>
            <DiffStat label="Документов добавлено"   value={data.documents.added.length}    color="#10B981" />
            <DiffStat label="Изменено"               value={data.documents.modified.length} color="#F59E0B" />
            <DiffStat label="Удалено"                value={data.documents.removed.length}  color="#EF4444" />
            <DiffStat label="Без изменений"          value={data.documents.unchanged_count} color="#6B7280" />
          </div>
          <div className="grid grid-cols-2" style={{ gap: 12, marginBottom: 20 }}>
            <RemStat label="Замечания «От»"
                     total={data.remarks.from.total} open={data.remarks.from.open} closed={data.remarks.from.closed} />
            <RemStat label="Замечания «До»"
                     total={data.remarks.to.total} open={data.remarks.to.open} closed={data.remarks.to.closed} />
          </div>

          <DiffList title="Добавленные документы" items={data.documents.added} tone="emerald" />
          <ModifiedDocsList items={data.documents.modified} projectId={projectId} fromId={fromId} toId={toId} />
          <DiffList title="Удалённые документы"   items={data.documents.removed} tone="rose" />
        </div>
      )}
    </div>
  );
}

interface ContentChange {
  status: 'added' | 'deleted' | 'modified';
  text_a: string | null;
  text_b: string | null;
  similarity: number;
  page_a: number | null;
  page_b: number | null;
  section_title?: string | null;
}

interface ContentDiffResult {
  summary: { added: number; deleted: number; modified: number };
  changes: ContentChange[];
}

function ModifiedDocsList({
  items,
  projectId,
  fromId,
  toId,
}: {
  items: DiffDoc[];
  projectId: string;
  fromId: string;
  toId: string;
}) {
  const [expandedDoc, setExpandedDoc] = useState<string | null>(null);

  const { data: contentDiff, isLoading: contentLoading, error: contentError } = useQuery<ContentDiffResult>({
    queryKey: ['content-diff', projectId, fromId, toId, expandedDoc],
    queryFn: () =>
      api.get<ContentDiffResult>(
        `/projects/${projectId}/snapshots/${fromId}/diff/content?to=${toId}&document_id=${expandedDoc}`,
      ),
    enabled: !!expandedDoc,
    retry: false,
  });

  if (!items.length) return null;
  const color = '#F59E0B';
  return (
    <details style={{ marginTop: 12 }}>
      <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0A1628' }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 8 }} />
        Изменённые документы ({items.length})
      </summary>
      <ul style={{ marginTop: 8, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((d) => (
          <li key={d.document_id}>
            <div className="flex items-center" style={{ gap: 10 }}>
              <span className="text-muted" style={{ fontSize: 12 }}>
                {d.title || `Документ ${d.document_id.slice(0, 8)}`}
                {d.from_version_seq != null && d.to_version_seq != null && (
                  <span className="tabular" style={{ marginLeft: 8, color: '#9CA3AF' }}>
                    v{d.from_version_seq} → v{d.to_version_seq}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => setExpandedDoc(expandedDoc === d.document_id ? null : d.document_id)}
                style={{
                  fontSize: 11, padding: '2px 8px', borderRadius: 4,
                  border: '1px solid #E5E7EB', background: 'transparent',
                  cursor: 'pointer', color: '#6B7280', whiteSpace: 'nowrap',
                }}
                title="Постраничное сравнение текста тома"
              >
                {expandedDoc === d.document_id ? 'Скрыть' : 'Показать изменения текста →'}
              </button>
            </div>
            {expandedDoc === d.document_id && (
              <div style={{ marginTop: 8, marginLeft: 0 }}>
                {contentLoading && <p className="text-muted" style={{ fontSize: 12 }}>Сравнение содержимого…</p>}
                {!contentLoading && contentError && (
                  <div style={{ padding: '10px 14px', borderRadius: 6, background: '#FFFBEB', border: '1px solid #FDE68A', fontSize: 12, color: '#92400E' }}>
                    <span style={{ fontWeight: 600 }}>Сравнение текста для этого тома недоступно.</span>{' '}
                    Не для всех форматов можно построить постраничное сравнение.
                    Сводка по составу выше (добавлено/изменено/удалено) работает всегда.
                  </div>
                )}
                {!contentLoading && !contentError && contentDiff && (
                  <div>
                    <div className="flex" style={{ gap: 8, marginBottom: 8, fontSize: 12, color: '#6B7280' }}>
                      <span style={{ color: '#10B981' }}>+ {contentDiff.summary.added} добавлено</span>
                      <span style={{ color: '#EF4444' }}>- {contentDiff.summary.deleted} удалено</span>
                      <span style={{ color: '#F59E0B' }}>~ {contentDiff.summary.modified} изменено</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {contentDiff.changes.slice(0, 20).map((ch, i) => (
                        <div
                          key={i}
                          style={{
                            padding: '8px 10px', borderRadius: 6, fontSize: 12, lineHeight: 1.5,
                            background: ch.status === 'added' ? '#F0FDF4' : ch.status === 'deleted' ? '#FEF2F2' : '#FFFBEB',
                            border: `1px solid ${ch.status === 'added' ? '#BBF7D0' : ch.status === 'deleted' ? '#FECACA' : '#FDE68A'}`,
                          }}
                        >
                          <div style={{ fontWeight: 700, marginBottom: 2, color: ch.status === 'added' ? '#15803D' : ch.status === 'deleted' ? '#B91C1C' : '#92400E' }}>
                            {ch.status === 'added' ? '+ Добавлено' : ch.status === 'deleted' ? '− Удалено' : `~ Изменено (схожесть: ${Math.round(ch.similarity * 100)}%)`}
                            {(ch.page_a || ch.page_b) ? ` · стр. ${ch.page_b ?? ch.page_a}` : ''}
                          </div>
                          {ch.section_title && <div style={{ fontStyle: 'italic', color: '#6B7280', marginBottom: 2 }}>{ch.section_title}</div>}
                          {ch.status === 'modified' && ch.text_a && (
                            <div style={{ color: '#9CA3AF', textDecoration: 'line-through', marginBottom: 4 }}>{ch.text_a.slice(0, 150)}</div>
                          )}
                          <div style={{ color: '#374151' }}>
                            {(ch.text_b ?? ch.text_a ?? '').slice(0, 200)}{(ch.text_b ?? ch.text_a ?? '').length > 200 ? '…' : ''}
                          </div>
                        </div>
                      ))}
                      {contentDiff.changes.length > 20 && (
                        <p className="text-muted" style={{ fontSize: 11 }}>Показано 20 из {contentDiff.changes.length}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

function DiffStat({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-card" style={{ padding: 12, background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
      <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 600 }}>{label}</div>
      <div className="font-heading tabular mt-1" style={{ fontSize: 22, fontWeight: 600, color }}>{value}</div>
    </div>
  );
}

function RemStat({ label, total, open, closed }: { label: string; total: number; open: number; closed: number }) {
  return (
    <div className="rounded-card" style={{ padding: 12, background: '#FFFFFF', border: '1px solid #E5E7EB' }}>
      <div className="text-muted uppercase tracking-wider" style={{ fontSize: 10, fontWeight: 600 }}>{label}</div>
      <div className="flex items-baseline mt-1" style={{ gap: 12 }}>
        <span className="font-heading tabular" style={{ fontSize: 22, fontWeight: 600, color: '#0A1628' }}>{total}</span>
        <span style={{ fontSize: 12, color: '#EF4444' }}>открыто: {open}</span>
        <span style={{ fontSize: 12, color: '#10B981' }}>закрыто: {closed}</span>
      </div>
    </div>
  );
}

function DiffList({ title, items, tone, showSeq }: {
  title: string; items: DiffDoc[]; tone: 'emerald' | 'amber' | 'rose'; showSeq?: boolean;
}) {
  if (!items.length) return null;
  const color = tone === 'emerald' ? '#10B981' : tone === 'amber' ? '#F59E0B' : '#EF4444';
  return (
    <details style={{ marginTop: 12 }}>
      <summary style={{ cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#0A1628' }}>
        <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: color, marginRight: 8 }} />
        {title} ({items.length})
      </summary>
      <ul style={{ marginTop: 8, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {items.map((d) => (
          <li key={d.document_id} className="text-muted" style={{ fontSize: 12 }}>
            {d.title || `Документ ${d.document_id.slice(0, 8)}`}
            {showSeq && d.from_version_seq != null && d.to_version_seq != null && (
              <span className="tabular" style={{ marginLeft: 8, color: '#9CA3AF' }}>
                v{d.from_version_seq} → v{d.to_version_seq}
              </span>
            )}
          </li>
        ))}
      </ul>
    </details>
  );
}

/* ─── helpers ───────────────────────────────────────────────────────────── */
function sevWeight(r: Remark): number {
  const w: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
  return w[normSeverity(r.severity)] || 0;
}
function trunc(s: string, max: number): string {
  return s.length <= max ? s : s.slice(0, max).trim() + '…';
}
function plural(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

// ── Журнал аудита ────────────────────────────────────────────────────────────

interface AuditEvent {
  id: string;
  event_type: string;
  payload: Record<string, unknown>;
  created_at: string;
  user_name: string | null;
  user_email: string | null;
}

const EVENT_LABELS: Record<string, string> = {
  remark_created:      'Создано замечание',
  response_created:    'Добавлен ответ',
  response_accepted:   'Ответ принят',
  response_rejected:   'Ответ отклонён',
  deadline_changed:    'Изменён срок сдачи',
};

const EVENT_COLORS: Record<string, string> = {
  remark_created:    '#6366F1',
  response_created:  '#3B82F6',
  response_accepted: '#10B981',
  response_rejected: '#EF4444',
  deadline_changed:  '#F59E0B',
};

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function AuditLogView({ projectId }: { projectId: string }) {
  const [offset, setOffset] = useState(0);
  const LIMIT = 30;

  const { data, isLoading, isError, refetch } = useQuery<{ events: AuditEvent[]; count: number }>({
    queryKey: ['audit', projectId, offset],
    queryFn: () => api.get(`/projects/${projectId}/audit?limit=${LIMIT}&offset=${offset}`),
    staleTime: 30_000,
  });

  const events = data?.events ?? [];

  return (
    <div>
      <div className="flex items-center justify-between" style={{ marginBottom: 16 }}>
        <div className="flex items-center" style={{ gap: 8 }}>
          <ClipboardList size={18} stroke="#6B7280" />
          <h4 className="font-heading text-ink" style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
            Журнал аудита
          </h4>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          style={{ padding: '4px 12px', fontSize: 12, background: 'transparent', border: '1px solid #E5E7EB', borderRadius: 6, cursor: 'pointer', color: '#6B7280' }}
        >
          Обновить
        </button>
      </div>

      {isLoading && (
        <p className="text-muted" style={{ fontSize: 13, padding: '32px 0', textAlign: 'center' }}>Загрузка…</p>
      )}
      {isError && (
        <p style={{ fontSize: 13, color: '#EF4444', padding: '16px 0' }}>Ошибка загрузки журнала.</p>
      )}
      {!isLoading && !isError && events.length === 0 && (
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <ClipboardList size={32} stroke="#D1D5DB" />
          <p className="text-muted" style={{ fontSize: 13, marginTop: 8 }}>
            События ещё не зафиксированы
          </p>
        </div>
      )}

      {events.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
          {events.map((ev, i) => {
            const color = EVENT_COLORS[ev.event_type] ?? '#6B7280';
            const label = EVENT_LABELS[ev.event_type] ?? ev.event_type;
            const who = ev.user_name || ev.user_email || 'Система';
            return (
              <div
                key={ev.id}
                style={{
                  display: 'flex', gap: 12, padding: '12px 0',
                  borderBottom: i < events.length - 1 ? '1px solid #F3F4F6' : 'none',
                }}
              >
                {/* Timeline dot */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                  {i < events.length - 1 && (
                    <div style={{ width: 1, flex: 1, background: '#E5E7EB', marginTop: 4, minHeight: 20 }} />
                  )}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="flex items-center" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color }}>
                      {label}
                    </span>
                    <span className="text-muted" style={{ fontSize: 12 }}>{fmtDate(ev.created_at)}</span>
                  </div>
                  <p className="text-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>{who}</p>
                  {ev.payload && Object.keys(ev.payload).length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {Object.entries(ev.payload)
                        .filter(([k]) => !['backfilled', 'remark_id', 'snapshot_id'].includes(k))
                        .map(([k, v]) => {
                          if (v === null || v === undefined) return null;
                          const sv = String(v);
                          const display: string =
                            k === 'defect_type' ? (sv === 'S-defect' ? 'Блокирующее' : sv === 'E-gap' ? 'Устранимое' : sv) :
                            k === 'deadline_at' ? (sv ? `Срок: ${sv}` : 'Срок снят') :
                            k === 'section_code' || k === 'severity' ? sv :
                            `${k}: ${sv.slice(0, 40)}`;
                          return (
                            <span
                              key={k}
                              style={{ fontSize: 11, padding: '2px 7px', background: '#F3F4F6', borderRadius: 4, color: '#374151' }}
                            >
                              {display}
                            </span>
                          );
                        })}
                      {ev.payload.remark_id != null && (
                        <span style={{ fontSize: 11, padding: '2px 7px', background: '#F3F4F6', borderRadius: 4, color: '#9CA3AF', fontFamily: 'monospace' }}>
                          #{String(ev.payload.remark_id as string).slice(0, 8)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {(events.length === LIMIT || offset > 0) && (
        <div className="flex items-center justify-center" style={{ gap: 12, marginTop: 20 }}>
          <button
            type="button"
            disabled={offset === 0}
            onClick={() => setOffset(Math.max(0, offset - LIMIT))}
            style={{
              padding: '5px 14px', fontSize: 12, borderRadius: 6,
              border: '1px solid #E5E7EB', background: 'transparent',
              cursor: offset === 0 ? 'default' : 'pointer',
              color: offset === 0 ? '#9CA3AF' : '#374151',
            }}
          >
            ← Новее
          </button>
          <button
            type="button"
            disabled={events.length < LIMIT}
            onClick={() => setOffset(offset + LIMIT)}
            style={{
              padding: '5px 14px', fontSize: 12, borderRadius: 6,
              border: '1px solid #E5E7EB', background: 'transparent',
              cursor: events.length < LIMIT ? 'default' : 'pointer',
              color: events.length < LIMIT ? '#9CA3AF' : '#374151',
            }}
          >
            Старее →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Маршрутизация замечаний ──────────────────────────────────────────────────

interface RoutingRule {
  id: string;
  domain: string;
  assignee_role: string | null;
  priority: string | null;
  reason: string | null;
  status: string | null;
  assigned_at: string | null;
  documents: Array<{ version_id: string; title: string }>;
}

const ROLE_OPTIONS = [
  { value: 'expert',  label: 'Эксперт' },
  { value: 'gip',     label: 'ГИП' },
  { value: 'curator', label: 'Куратор' },
];

interface AutoPreviewAssignment {
  remark_id: string;
  remark_body_preview: string;
  specialist_role: string | null;
  suggested_user_id: string;
  suggested_user_name: string | null;
  suggested_user_email: string | null;
  suggested_user_section: string | null;
}
interface AutoPreviewUnmatched {
  remark_id: string;
  remark_body_preview: string;
  specialist_role: string | null;
  reason: string;
}
interface AutoPreviewResponse {
  assignments: AutoPreviewAssignment[];
  unmatched: AutoPreviewUnmatched[];
  eligible_users: Array<{ id: string; email: string; full_name: string | null; expert_section: string | null }>;
}

function RoutingView({
  projectId,
  snapshotId,
  remarks,
}: {
  projectId: string;
  snapshotId: string | null;
  remarks: Remark[];
}) {
  const qc = useQueryClient();
  const [newDomain, setNewDomain] = useState('');
  const [newRole, setNewRole] = useState('expert');
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [autoPreview, setAutoPreview] = useState<AutoPreviewResponse | null>(null);
  const [autoLoading, setAutoLoading] = useState(false);
  const [autoApplying, setAutoApplying] = useState(false);
  const [autoErr, setAutoErr] = useState('');
  const [autoApplied, setAutoApplied] = useState<number | null>(null);

  const { data, isLoading } = useQuery<{ assignments: RoutingRule[] }>({
    queryKey: ['routing', projectId],
    queryFn: () => api.get(`/projects/${projectId}/routing`),
    staleTime: 30_000,
  });

  const rules = data?.assignments ?? [];

  // Auto-routing stats from remarks
  const autoRouted = remarks.filter((r) => r.specialist_role).length;
  const notRouted = remarks.filter((r) => !r.specialist_role).length;

  async function addRule() {
    if (!newDomain.trim()) return;
    if (!snapshotId) { setSaveErr('Нет активного снапшота'); return; }
    setSaving(true);
    setSaveErr('');
    try {
      await api.post(`/projects/${projectId}/route`, {
        snapshot_id: snapshotId,
        assignments: [
          { domain: newDomain.trim().toUpperCase(), assignee_role: newRole },
          ...rules.map((r) => ({
            domain: r.domain,
            assignee_role: r.assignee_role,
            priority: r.priority,
            reason: r.reason,
          })),
        ],
      });
      setNewDomain('');
      qc.invalidateQueries({ queryKey: ['routing', projectId] });
    } catch (e: unknown) {
      setSaveErr(String(e));
    } finally {
      setSaving(false);
    }
  }

  async function removeRule(domain: string) {
    if (!snapshotId) return;
    const remaining = rules
      .filter((r) => r.domain !== domain)
      .map((r) => ({
        domain: r.domain,
        assignee_role: r.assignee_role,
        priority: r.priority,
        reason: r.reason,
      }));
    if (remaining.length === 0) {
      // POST requires min 1 item; mark as inactive instead
      setSaveErr('Нельзя удалить последнее правило — измените assignee_role');
      return;
    }
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/route`, {
        snapshot_id: snapshotId,
        assignments: remaining,
      });
      qc.invalidateQueries({ queryKey: ['routing', projectId] });
    } catch (e: unknown) {
      setSaveErr(String(e));
    } finally {
      setSaving(false);
    }
  }

  async function runAutoPreview() {
    if (!snapshotId) return;
    setAutoLoading(true);
    setAutoErr('');
    setAutoPreview(null);
    setAutoApplied(null);
    try {
      const res = await api.get<AutoPreviewResponse>(
        `/projects/${projectId}/route/preview?snapshot_id=${snapshotId}`
      );
      setAutoPreview(res);
    } catch (e: unknown) {
      setAutoErr(e instanceof Error ? e.message : 'Не удалось получить предпросмотр');
    } finally {
      setAutoLoading(false);
    }
  }

  async function applyAutoRouting() {
    if (!snapshotId || !autoPreview) return;
    setAutoApplying(true);
    setAutoErr('');
    try {
      const payload = {
        snapshot_id: snapshotId,
        assignments: autoPreview.assignments.map((a) => ({
          remark_id: a.remark_id,
          expert_user_id: a.suggested_user_id,
        })),
      };
      const res = await api.post<{ applied: number; errors: Array<{ remark_id: string; msg: string }> }>(
        `/projects/${projectId}/route/apply`, payload
      );
      setAutoApplied(res.applied);
      if (res.errors?.length) {
        setAutoErr(`Назначено: ${res.applied}. Ошибок: ${res.errors.length}`);
      }
      qc.invalidateQueries({ queryKey: ['remarks', projectId] });
    } catch (e: unknown) {
      setAutoErr(e instanceof Error ? e.message : 'Ошибка применения');
    } finally {
      setAutoApplying(false);
    }
  }

  return (
    <div>
      <div className="flex items-center" style={{ gap: 8, marginBottom: 20 }}>
        <GitMerge size={18} stroke="#6B7280" />
        <h4 className="font-heading text-ink" style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
          Маршрутизация замечаний
        </h4>
      </div>

      {/* Stats */}
      <div className="flex" style={{ gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ padding: '10px 16px', background: '#F0FDF4', borderRadius: 8, border: '1px solid #BBF7D0' }}>
          <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>Назначено автоматически</p>
          <p style={{ fontSize: 22, fontWeight: 700, color: '#10B981', margin: '2px 0 0' }}>{autoRouted}</p>
        </div>
        <div style={{ padding: '10px 16px', background: notRouted > 0 ? '#FEF3C7' : '#F9FAFB', borderRadius: 8, border: `1px solid ${notRouted > 0 ? '#FDE68A' : '#E5E7EB'}` }}>
          <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>Без исполнителя</p>
          <p style={{ fontSize: 22, fontWeight: 700, color: notRouted > 0 ? '#F59E0B' : '#9CA3AF', margin: '2px 0 0' }}>{notRouted}</p>
        </div>
        <div style={{ padding: '10px 16px', background: '#F3F4F6', borderRadius: 8, border: '1px solid #E5E7EB' }}>
          <p style={{ fontSize: 11, color: '#6B7280', margin: 0 }}>Правил маршрутизации</p>
          <p style={{ fontSize: 22, fontWeight: 700, color: '#374151', margin: '2px 0 0' }}>{rules.length}</p>
        </div>
      </div>

      {/* Rules table */}
      {isLoading && <p className="text-muted" style={{ fontSize: 13 }}>Загрузка…</p>}
      {!isLoading && rules.length === 0 && (
        <div style={{ padding: '24px', background: '#F9FAFB', borderRadius: 8, textAlign: 'center', marginBottom: 20 }}>
          <p className="text-muted" style={{ fontSize: 13 }}>Правила не настроены. Добавьте первое правило ниже.</p>
        </div>
      )}
      {rules.length > 0 && (
        <div style={{ marginBottom: 20, borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #E5E7EB' }}>Раздел/домен</th>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #E5E7EB' }}>Роль исполнителя</th>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #E5E7EB' }}>Документы</th>
                <th style={{ padding: '8px 14px', borderBottom: '1px solid #E5E7EB' }} />
              </tr>
            </thead>
            <tbody>
              {rules.map((rule, i) => (
                <tr key={rule.id} style={{ borderBottom: i < rules.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>
                    <span style={{
                      display: 'inline-block',
                      padding: '2px 8px',
                      borderRadius: 4,
                      background: rule.domain === '*' ? '#F3F4F6' : '#EFF6FF',
                      color: rule.domain === '*' ? '#6B7280' : '#1D4ED8',
                      fontSize: 12,
                    }}>
                      {rule.domain === '*' ? '* (любой раздел)' : rule.domain}
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px', color: '#374151' }}>
                    {ROLE_OPTIONS.find((r) => r.value === rule.assignee_role)?.label ?? rule.assignee_role ?? '—'}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#6B7280', fontSize: 12 }}>
                    {rule.documents.length > 0
                      ? rule.documents.map((d) => d.title).join(', ')
                      : '—'}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => removeRule(rule.domain)}
                      disabled={saving}
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#EF4444', padding: 4 }}
                      title="Удалить правило"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add rule form */}
      {snapshotId && (
        <div style={{ padding: '16px', background: '#F9FAFB', borderRadius: 8, border: '1px solid #E5E7EB' }}>
          <p style={{ fontSize: 13, fontWeight: 600, margin: '0 0 12px', color: '#374151' }}>Добавить правило</p>
          <div className="flex items-center" style={{ gap: 10, flexWrap: 'wrap' }}>
            <input
              type="text"
              placeholder="Раздел (ТКР, ООС, * для всех)"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              style={{ padding: '7px 12px', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 6, minWidth: 180, outline: 'none' }}
            />
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              style={{ padding: '7px 12px', fontSize: 13, border: '1px solid #E5E7EB', borderRadius: 6, outline: 'none', cursor: 'pointer' }}
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={addRule}
              disabled={saving || !newDomain.trim()}
              className="inline-flex items-center"
              style={{
                padding: '7px 16px', fontSize: 13, fontWeight: 600,
                background: '#F59E0B', color: '#0A1628',
                border: 'none', borderRadius: 6, cursor: saving || !newDomain.trim() ? 'default' : 'pointer',
                opacity: saving || !newDomain.trim() ? 0.6 : 1, gap: 6,
              }}
            >
              <Plus size={14} />
              {saving ? 'Сохранение…' : 'Добавить'}
            </button>
          </div>
          {saveErr && <p style={{ fontSize: 12, color: '#EF4444', marginTop: 8 }}>{saveErr}</p>}
          <p className="text-muted" style={{ fontSize: 11, marginTop: 8 }}>
            Правила применяются при создании нового замечания. Wildcards (*) — самый низкий приоритет.
          </p>
        </div>
      )}
      {!snapshotId && (
        <p className="text-muted" style={{ fontSize: 12 }}>Для настройки правил нужна активная редакция.</p>
      )}

      {/* ── Auto-routing section ─────────────────────────────────────────── */}
      <div style={{ marginTop: 28, borderTop: '1px solid #E5E7EB', paddingTop: 24 }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
          <div>
            <h5 style={{ fontSize: 14, fontWeight: 700, margin: 0, color: '#0A1628' }}>Авто-маршрутизация</h5>
            <p className="text-muted" style={{ fontSize: 12, margin: '4px 0 0' }}>
              Алгоритм подбирает эксперта к каждому открытому замечанию по разделу и специализации.
            </p>
          </div>
          {snapshotId && (
            <button
              type="button"
              onClick={runAutoPreview}
              disabled={autoLoading}
              className="inline-flex items-center"
              style={{
                padding: '7px 14px', fontSize: 13, fontWeight: 600,
                background: '#6366F1', color: '#FFF', border: 'none', borderRadius: 6,
                cursor: autoLoading ? 'default' : 'pointer', opacity: autoLoading ? 0.7 : 1, gap: 6,
              }}
            >
              <Sparkles size={14} />
              {autoLoading ? 'Анализ…' : 'Предпросмотр назначений'}
            </button>
          )}
        </div>

        {autoErr && (
          <p style={{ fontSize: 12, color: '#EF4444', marginBottom: 8 }}>{autoErr}</p>
        )}
        {autoApplied !== null && !autoErr && (
          <div style={{ padding: '8px 14px', background: '#F0FDF4', borderRadius: 6, border: '1px solid #BBF7D0', fontSize: 13, color: '#15803D', marginBottom: 12 }}>
            Назначено успешно: {autoApplied} замечаний
          </div>
        )}

        {autoPreview && (
          <div style={{ borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden', marginBottom: 16 }}>
            {autoPreview.assignments.length === 0 && autoPreview.unmatched.length === 0 && (
              <p className="text-muted" style={{ padding: '16px 20px', fontSize: 13 }}>
                Нет открытых замечаний для назначения.
              </p>
            )}
            {autoPreview.assignments.length > 0 && (
              <>
                <div style={{ padding: '10px 16px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', fontSize: 12, fontWeight: 600, color: '#374151', display: 'flex', justifyContent: 'space-between' }}>
                  <span>Предложенные назначения ({autoPreview.assignments.length})</span>
                  <button
                    type="button"
                    onClick={applyAutoRouting}
                    disabled={autoApplying}
                    style={{
                      padding: '3px 12px', fontSize: 12, fontWeight: 600,
                      background: '#10B981', color: '#FFF', border: 'none', borderRadius: 4,
                      cursor: autoApplying ? 'default' : 'pointer', opacity: autoApplying ? 0.7 : 1,
                    }}
                  >
                    {autoApplying ? 'Применение…' : `Применить (${autoPreview.assignments.length})`}
                  </button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: '#F9FAFB' }}>
                      <th style={{ padding: '6px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #F3F4F6' }}>Замечание</th>
                      <th style={{ padding: '6px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #F3F4F6' }}>Раздел</th>
                      <th style={{ padding: '6px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #F3F4F6' }}>Эксперт</th>
                      <th style={{ padding: '6px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #F3F4F6' }}>Специализация</th>
                    </tr>
                  </thead>
                  <tbody>
                    {autoPreview.assignments.map((a, i) => (
                      <tr key={a.remark_id} style={{ borderBottom: i < autoPreview.assignments.length - 1 ? '1px solid #F9FAFB' : 'none' }}>
                        <td style={{ padding: '8px 14px', color: '#374151', maxWidth: 240 }}>{a.remark_body_preview}</td>
                        <td style={{ padding: '8px 14px' }}>
                          {a.specialist_role ? (
                            <span style={{ fontSize: 11, padding: '1px 6px', borderRadius: 4, background: '#EFF6FF', color: '#1D4ED8' }}>{a.specialist_role}</span>
                          ) : '—'}
                        </td>
                        <td style={{ padding: '8px 14px', color: '#0A1628', fontWeight: 500 }}>
                          {a.suggested_user_name || a.suggested_user_email}
                        </td>
                        <td style={{ padding: '8px 14px', color: '#6B7280' }}>{a.suggested_user_section ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
            {autoPreview.unmatched.length > 0 && (
              <div style={{ padding: '10px 16px', background: '#FEF3C7', borderTop: autoPreview.assignments.length > 0 ? '1px solid #FDE68A' : 'none' }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#92400E', marginBottom: 6 }}>Не удалось назначить ({autoPreview.unmatched.length})</div>
                {autoPreview.unmatched.map((u) => (
                  <div key={u.remark_id} style={{ fontSize: 11, color: '#78350F', marginBottom: 3 }}>
                    {u.remark_body_preview} — <em>{u.reason}</em>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Summary ────────────────────────────────────────────────────────────── */
interface ProjectDetail { id: string; name: string; deadline_at: string | null; object_type: string | null; created_at: string | null }
interface AuditEntry { id: string; event_type: string; payload: Record<string, unknown>; created_at: string; user_email?: string | null }
interface AuditListResponse { events: AuditEntry[] }

function SummaryView({ projectId, snapshotId, remarks, summary, snapshots }: {
  projectId: string;
  snapshotId: string | null;
  remarks: Remark[];
  summary?: RemarksDashboardResponse['summary'];
  snapshots: Snapshot[];
}) {
  const { data: project } = useQuery<ProjectDetail>({
    queryKey: ['project-detail', projectId],
    queryFn: () => api.get<ProjectDetail>(`/projects/${projectId}`),
    staleTime: 60_000,
  });

  const { data: auditData } = useQuery<AuditListResponse>({
    queryKey: ['audit-last', projectId],
    queryFn: () => api.get<AuditListResponse>(`/projects/${projectId}/audit?limit=1`),
    staleTime: 30_000,
  });

  const isOpen = (r: Remark) => (r.remark_status || (r.closed_at ? 'closed' : 'open')) !== 'closed';
  const total = remarks.length;
  const openCount = remarks.filter(isOpen).length;
  const closedCount = total - openCount;
  const sOpen = remarks.filter((r) => r.defect_type === 'S-defect' && isOpen(r)).length;
  const eOpen = remarks.filter((r) => r.defect_type === 'E-gap' && isOpen(r)).length;
  const readiness = total === 0 ? 100 : Math.round((closedCount / total) * 100);
  const readinessColor = readiness >= 80 ? '#10B981' : readiness >= 50 ? '#F59E0B' : '#EF4444';

  const sections = new Set(remarks.map((r) => r.section_code).filter(Boolean)).size;

  // Deadline
  const deadlineStr = project?.deadline_at ?? null;
  let daysLeft: number | null = null;
  let deadlineLabel = '—';
  if (deadlineStr) {
    const dl = new Date(deadlineStr);
    const now = new Date();
    daysLeft = Math.ceil((dl.getTime() - now.getTime()) / 86400000);
    deadlineLabel = dl.toLocaleDateString('ru-RU', { day: '2-digit', month: 'long', year: 'numeric' });
  }
  const deadlineColor = daysLeft === null ? '#9CA3AF' : daysLeft < 0 ? '#EF4444' : daysLeft <= 7 ? '#F59E0B' : '#10B981';

  const lastEvent = auditData?.events?.[0] ?? null;
  const eventLabels: Record<string, string> = {
    deadline_changed: 'Изменён срок', remark_created: 'Создано замечание',
    remark_auto_routed: 'Авто-маршрутизация', response_created: 'Добавлен ответ',
    response_accepted: 'Ответ принят', response_rejected: 'Ответ отклонён',
  };

  const snapshotCount = snapshots.length;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 20 }}>
      {/* Block 1: Readiness score */}
      <SummaryBlock title="Готовность проекта" accent={readinessColor}>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          <div style={{ fontSize: 52, fontWeight: 700, color: readinessColor, lineHeight: 1 }}>{readiness}%</div>
          <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
            {closedCount} из {total} замечаний закрыто
          </div>
          <div style={{ marginTop: 12, height: 8, borderRadius: 4, background: '#F3F4F6', overflow: 'hidden' }}>
            <div style={{ width: `${readiness}%`, height: '100%', background: readinessColor, borderRadius: 4, transition: 'width 0.5s' }} />
          </div>
          {sOpen > 0 && (
            <div className="flex items-center justify-center" style={{ gap: 6, marginTop: 10, fontSize: 12 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#EF4444', display: 'inline-block' }} />
              <span style={{ color: '#B91C1C', fontWeight: 600 }}>{sOpen} блокирующих открыто</span>
            </div>
          )}
        </div>
      </SummaryBlock>

      {/* Block 2: Remarks breakdown */}
      <SummaryBlock title="Замечания" accent="#6366F1">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          <SummaryRow label="Всего" value={String(total)} />
          <SummaryRow label="Открытых" value={String(openCount)} valueColor={openCount > 0 ? '#F59E0B' : '#10B981'} />
          <SummaryRow label="Блокирующих (С)" value={String(sOpen)} valueColor={sOpen > 0 ? '#EF4444' : '#10B981'} />
          <SummaryRow label="Устранимых (Е)" value={String(eOpen)} valueColor={eOpen > 0 ? '#F59E0B' : '#10B981'} />
          <SummaryRow label="Закрытых" value={String(closedCount)} valueColor="#10B981" />
          <SummaryRow label="Разделов с замечаниями" value={sections > 0 ? String(sections) : '—'} />
          <SummaryRow label="Норм-соответствие (из summary)" value={summary ? `С: ${summary.s_defects ?? sOpen}  Е: ${summary.e_gaps ?? eOpen}` : '—'} />
        </div>
      </SummaryBlock>

      {/* Block 3: Snapshots / versions */}
      <SummaryBlock title="Редакции проекта" accent="#10B981">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
          <SummaryRow label="Всего редакций" value={String(snapshotCount)} />
          <SummaryRow label="Активная редакция" value={snapshotId ? 'Выбрана' : 'Не выбрана'} valueColor={snapshotId ? '#10B981' : '#F59E0B'} />
          {snapshots.length > 0 && (
            <div style={{ marginTop: 4 }}>
              <div className="text-muted" style={{ fontSize: 11, marginBottom: 4 }}>Последние:</div>
              {snapshots.slice(-3).reverse().map((s) => (
                <div key={s.id} style={{ fontSize: 11, padding: '3px 0', display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#374151' }}>#{s.id.slice(0, 6)}</span>
                  <span className="text-muted">{s.created_at ? new Date(s.created_at).toLocaleDateString('ru-RU') : '—'}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </SummaryBlock>

      {/* Block 4: Deadline */}
      <SummaryBlock title="Срок сдачи" accent={deadlineColor}>
        <div style={{ textAlign: 'center', padding: '16px 0' }}>
          {deadlineStr ? (
            <>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#0A1628' }}>{deadlineLabel}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: deadlineColor, marginTop: 8 }}>
                {daysLeft === null ? '—' : daysLeft < 0 ? `Просрочено на ${Math.abs(daysLeft)} дн.` : daysLeft === 0 ? 'Сегодня' : `${daysLeft} дн.`}
              </div>
              <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                {daysLeft !== null && daysLeft < 0 ? 'Срок истёк' : daysLeft !== null && daysLeft <= 7 ? 'Срок скоро истекает' : 'До окончания экспертизы'}
              </div>
            </>
          ) : (
            <div className="text-muted" style={{ fontSize: 13 }}>Срок не установлен</div>
          )}
        </div>
      </SummaryBlock>

      {/* Block 5: Last action */}
      <SummaryBlock title="Последнее действие" accent="#9CA3AF">
        {lastEvent ? (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
              {eventLabels[lastEvent.event_type] ?? lastEvent.event_type}
            </div>
            {lastEvent.user_email && (
              <div className="text-muted" style={{ fontSize: 11, marginTop: 2 }}>{lastEvent.user_email}</div>
            )}
            <div className="text-muted" style={{ fontSize: 11, marginTop: 6 }}>
              {new Date(lastEvent.created_at).toLocaleString('ru-RU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
            </div>
            {lastEvent.event_type === 'remark_created' && (() => {
              const sc = lastEvent.payload?.['section_code'];
              return sc ? <div style={{ marginTop: 6, padding: '4px 8px', background: '#F3F4F6', borderRadius: 4, fontSize: 11, display: 'inline-block' }}>Раздел: {String(sc)}</div> : null;
            })()}
          </div>
        ) : (
          <div className="text-muted" style={{ fontSize: 12, marginTop: 12 }}>Действий пока нет</div>
        )}
      </SummaryBlock>
    </div>
  );
}

function SummaryBlock({ title, accent, children }: { title: string; accent: string; children: ReactNode }) {
  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 20, borderLeft: `3px solid ${accent}` }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  );
}

function SummaryRow({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
      <span className="text-muted">{label}</span>
      <span style={{ fontWeight: 600, color: valueColor ?? '#0A1628' }}>{value}</span>
    </div>
  );
}

/* ─── AI View ────────────────────────────────────────────────────────────── */

interface AIViewConfig {
  filterDefectType: 'all' | 'S-defect' | 'E-gap';
  filterStatus: 'all' | 'open' | 'closed';
  filterSection: string | null;
  sortBy: 'created_at_desc' | 'created_at_asc' | 'section' | 'severity';
  limit: number | null;
  label: string; // human description of current config
}

const DEFAULT_CONFIG: AIViewConfig = {
  filterDefectType: 'all', filterStatus: 'all', filterSection: null,
  sortBy: 'created_at_desc', limit: null, label: 'Все замечания',
};

const AI_TEMPLATES: Array<{ id: string; name: string; desc: string; icon: string; config: Partial<AIViewConfig> }> = [
  {
    id: 'blocking',
    name: 'Блокирующие',
    desc: 'Открытые С-дефекты — самые критичные',
    icon: '🚨',
    config: { filterDefectType: 'S-defect', filterStatus: 'open', sortBy: 'section', label: 'Блокирующие открытые (С-дефекты)' },
  },
  {
    id: 'fixable',
    name: 'Устранимые',
    desc: 'Открытые Е-замечания — можно устранить',
    icon: '⚡',
    config: { filterDefectType: 'E-gap', filterStatus: 'open', sortBy: 'section', label: 'Устранимые открытые (Е-замечания)' },
  },
  {
    id: 'by_section',
    name: 'По разделам',
    desc: 'Все открытые, сгруппированные по разделу',
    icon: '📂',
    config: { filterStatus: 'open', sortBy: 'section', label: 'По разделам (открытые)' },
  },
  {
    id: 'recent',
    name: 'Последние 20',
    desc: 'Последние добавленные замечания',
    icon: '🕐',
    config: { filterDefectType: 'all', filterStatus: 'all', sortBy: 'created_at_desc', limit: 20, label: 'Последние 20 замечаний' },
  },
  {
    id: 'closed',
    name: 'Закрытые',
    desc: 'Все устранённые замечания',
    icon: '✅',
    config: { filterStatus: 'closed', sortBy: 'created_at_desc', label: 'Закрытые замечания' },
  },
];

/** NL parser — derives a view config from a Russian natural-language query */
function parseNLQuery(q: string, existing: AIViewConfig): AIViewConfig {
  const t = q.toLowerCase();
  const cfg: AIViewConfig = { ...existing, label: `Запрос: «${q}»` };

  // Status
  if (/открыт|незакрыт|нерешён|pending/.test(t)) cfg.filterStatus = 'open';
  else if (/закрыт|решён|устранён|resolved/.test(t)) cfg.filterStatus = 'closed';
  else if (/все|all/.test(t)) cfg.filterStatus = 'all';

  // Defect type
  if (/блокирующ|с-дефект|критич|s.defect/.test(t)) cfg.filterDefectType = 'S-defect';
  else if (/устраним|е-замеч|несущественн|e.gap/.test(t)) cfg.filterDefectType = 'E-gap';
  else if (/все типы|all/.test(t)) cfg.filterDefectType = 'all';

  // Section — look for known codes
  const knownSections = ['ПЗ', 'ППО', 'ТКР', 'ПЖ', 'ПОС', 'ООС', 'ПБ', 'СМ', 'ИЛО', 'ТБЭ', 'ГТ', 'ВК'];
  for (const sec of knownSections) {
    if (q.toUpperCase().includes(sec)) { cfg.filterSection = sec; break; }
  }
  if (/без раздела|no section/.test(t)) cfg.filterSection = null;

  // Sort
  if (/по дате|последн|новейш|recent/.test(t)) cfg.sortBy = 'created_at_desc';
  else if (/старые|с начала|первые|oldest/.test(t)) cfg.sortBy = 'created_at_asc';
  else if (/по разделу|по секции|section/.test(t)) cfg.sortBy = 'section';
  else if (/по критичн|по важн|severity/.test(t)) cfg.sortBy = 'severity';

  // Limit
  const limMatch = t.match(/последние?\s*(\d+)|первые?\s*(\d+)|топ\s*(\d+)/);
  if (limMatch) cfg.limit = parseInt(limMatch[1] ?? limMatch[2] ?? limMatch[3]);
  else if (!/последн|recent/.test(t)) cfg.limit = null;

  return cfg;
}

function applyConfig(remarks: Remark[], cfg: AIViewConfig): Remark[] {
  let result = [...remarks];

  if (cfg.filterStatus === 'open')
    result = result.filter((r) => (r.remark_status || (r.closed_at ? 'closed' : 'open')) !== 'closed');
  else if (cfg.filterStatus === 'closed')
    result = result.filter((r) => (r.remark_status || (r.closed_at ? 'closed' : 'open')) === 'closed');

  if (cfg.filterDefectType !== 'all')
    result = result.filter((r) => r.defect_type === cfg.filterDefectType);

  if (cfg.filterSection)
    result = result.filter((r) => (r.section_code || '').toUpperCase().startsWith(cfg.filterSection!));

  if (cfg.sortBy === 'created_at_desc')
    result.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
  else if (cfg.sortBy === 'created_at_asc')
    result.sort((a, b) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime());
  else if (cfg.sortBy === 'section')
    result.sort((a, b) => (a.section_code || '').localeCompare(b.section_code || '', 'ru'));
  else if (cfg.sortBy === 'severity')
    result.sort((a, b) => sevWeight(b) - sevWeight(a));

  if (cfg.limit) result = result.slice(0, cfg.limit);

  return result;
}

function AIView({ remarks }: { remarks: Remark[] }) {
  const [config, setConfig] = useState<AIViewConfig>(DEFAULT_CONFIG);
  const [nlQuery, setNlQuery] = useState('');
  const [nlResult, setNlResult] = useState<string | null>(null);

  const applyTemplate = (t: typeof AI_TEMPLATES[0]) => {
    setConfig({ ...DEFAULT_CONFIG, ...t.config } as AIViewConfig);
    setNlQuery('');
    setNlResult(null);
  };

  const handleNL = () => {
    const q = nlQuery.trim();
    if (!q) return;
    const newCfg = parseNLQuery(q, DEFAULT_CONFIG);
    setConfig(newCfg);
    setNlResult(newCfg.label);
  };

  const shown = applyConfig(remarks, config);

  return (
    <div>
      <header style={{ marginBottom: 20 }}>
        <div className="flex items-center" style={{ gap: 10, marginBottom: 4 }}>
          <Sparkles size={18} stroke="#6366F1" />
          <h4 className="font-heading text-ink" style={{ fontSize: 18, fontWeight: 600 }}>ИИ-вид — умная фильтрация замечаний</h4>
        </div>
        <p className="text-muted" style={{ fontSize: 13 }}>
          Выберите шаблон вида или задайте запрос на русском языке — система автоматически настроит фильтры и сортировку.
        </p>
      </header>

      {/* Templates */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
        {AI_TEMPLATES.map((tpl) => (
          <button
            key={tpl.id}
            type="button"
            onClick={() => applyTemplate(tpl)}
            className="flex flex-col items-start"
            style={{
              padding: '12px 16px', borderRadius: 10, border: config.label === (DEFAULT_CONFIG.label ? DEFAULT_CONFIG.label : tpl.config.label) ? '1.5px solid #6366F1' : '1px solid #E5E7EB',
              background: '#FFFFFF', cursor: 'pointer', minWidth: 140, textAlign: 'left', gap: 4,
              boxShadow: '0 1px 3px rgba(0,0,0,.04)',
            }}
          >
            <span style={{ fontSize: 20 }}>{tpl.icon}</span>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#0A1628' }}>{tpl.name}</span>
            <span style={{ fontSize: 11, color: '#9CA3AF', lineHeight: 1.35 }}>{tpl.desc}</span>
          </button>
        ))}
      </div>

      {/* NL input */}
      <div style={{ marginBottom: 20, padding: 16, background: '#F9FAFB', borderRadius: 10, border: '1px solid #E5E7EB' }}>
        <div className="flex items-center" style={{ gap: 8, marginBottom: 8 }}>
          <Search size={14} stroke="#6366F1" />
          <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Запрос на русском языке</span>
        </div>
        <div className="flex items-center" style={{ gap: 8 }}>
          <input
            type="text"
            value={nlQuery}
            onChange={(e) => setNlQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleNL(); }}
            placeholder="Например: «покажи блокирующие по разделу ТКР» или «последние 10 открытых устранимых»"
            className="w-full outline-none bg-white border border-border rounded-btn"
            style={{ padding: '9px 14px', fontSize: 13, flex: 1 }}
          />
          <button
            type="button"
            onClick={handleNL}
            disabled={!nlQuery.trim()}
            style={{
              padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              background: '#6366F1', color: '#FFF', border: 'none', cursor: nlQuery.trim() ? 'pointer' : 'default',
              opacity: nlQuery.trim() ? 1 : 0.5, flexShrink: 0,
            }}
          >
            Применить
          </button>
        </div>
        {nlResult && (
          <div className="flex items-center" style={{ gap: 6, marginTop: 8, fontSize: 11 }}>
            <Sparkles size={12} stroke="#6366F1" />
            <span style={{ color: '#6366F1', fontWeight: 500 }}>ИИ-интерпретация: {nlResult}</span>
          </div>
        )}
      </div>

      {/* Active config badge */}
      <div className="flex items-center" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, background: '#EEF2FF', color: '#4338CA', fontWeight: 600 }}>
          {config.label}
        </span>
        {config.filterSection && (
          <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 10, background: '#F0FDF4', color: '#15803D', fontWeight: 600 }}>
            Раздел: {config.filterSection}
          </span>
        )}
        <span className="text-muted" style={{ fontSize: 11 }}>{shown.length} замечаний</span>
        <button
          type="button"
          onClick={() => { setConfig(DEFAULT_CONFIG); setNlQuery(''); setNlResult(null); }}
          style={{ fontSize: 11, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer', marginLeft: 'auto' }}
        >
          Сбросить
        </button>
      </div>

      {/* Results table */}
      {shown.length === 0 ? (
        <div className="text-muted text-center" style={{ padding: '32px', fontSize: 13 }}>
          Замечаний по заданным критериям не найдено.
        </div>
      ) : (
        <div style={{ borderRadius: 8, border: '1px solid #E5E7EB', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#F9FAFB' }}>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #E5E7EB', width: 70 }}>Тип</th>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #E5E7EB', width: 80 }}>Раздел</th>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #E5E7EB' }}>Замечание</th>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: '#6B7280', fontWeight: 500, borderBottom: '1px solid #E5E7EB', width: 80 }}>Статус</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r, idx) => {
                const isOpen = (r.remark_status || (r.closed_at ? 'closed' : 'open')) !== 'closed';
                const isS = r.defect_type === 'S-defect';
                return (
                  <tr key={r.id} style={{ background: idx % 2 === 0 ? '#FFFFFF' : '#FAFAFA', borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '8px 14px' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                        background: isS ? '#FEF2F2' : '#FFFBEB',
                        color: isS ? '#B91C1C' : '#92400E',
                      }}>
                        {isS ? 'С' : 'Е'}
                      </span>
                    </td>
                    <td style={{ padding: '8px 14px', fontWeight: 500, color: '#374151', fontSize: 12 }}>
                      {r.section_code || '—'}
                    </td>
                    <td style={{ padding: '8px 14px', color: '#0A1628', lineHeight: 1.45 }}>
                      {(r.body || '').slice(0, 120)}{(r.body || '').length > 120 ? '…' : ''}
                    </td>
                    <td style={{ padding: '8px 14px' }}>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
                        background: isOpen ? '#FFFBEB' : '#F0FDF4',
                        color: isOpen ? '#92400E' : '#15803D',
                      }}>
                        {isOpen ? 'Откр.' : 'Закр.'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
